/* eslint-disable no-template-curly-in-string */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// ============================================================
// 📚 GUIDE TRANSLATIONS (БАЗА ЗНАНИЙ)
// ============================================================

const guideEN = {
  nav: { title: "Table of Contents", intro: "Intro & Exchanges", start: "Quick Start", modes: "Strategy Modes", signals: "Signal Types V2.0", logic: "TrendGuard Algo", risk: "Risk & Paper Trading", logs: "System Logs", telegram: "Telegram", trouble: "Troubleshooting", faq: "FAQ" },
  header: { title: "Knowledge Base v1.5", desc: "Complete documentation for Trading Bot Pro. Learn how the system earns on Binance and Bybit, how TrendGuard™ protection works, and how to manage risks." },
  intro: { 
    title: "What can this bot do?", 
    text: "This is an autonomous trading system for the Futures market. It analyzes the market 24/7 and finds entry points using technical analysis (RSI, EMA, Bollinger) and Price Action.",
    f1: "Support for Binance Futures and Bybit Futures. Easy switching in settings.",
    f2: "New protection system (V6.4). The bot analyzes EMA 200 and prevents opening trades against the global trend.",
    f3: "Entry only via limit orders. This saves fees and protects against slippage.",
    f4_t: "Crash Protection", f4_d: "Trading is blocked during extreme Bitcoin volatility or market panic."
  },
  start: {
    title: "Quick Start",
    s1_t: "API Keys Setup", s1_l1: "Create keys on Binance or Bybit.", s1_l2: "Enable 'Enable Futures'.", s1_l3: "Disable 'Withdrawal' access.",
    s2_t: "Connection", s2_d: "In 'Settings', select exchange and enter keys.",
    s3_t: "Trading Mode", s3_d: "We recommend starting with Paper Trading (Simulation with virtual $10,000)."
  },
  modes: {
    title: "Strategy Modes (Pro)", desc: "You can switch bot logic directly from the Dashboard header.",
    auto: "Hybrid mode. Balance between trend trades and bounces. Recommended for most.",
    trend: "Conservative mode. Hard EMA 200 filter. Bot trades ONLY in the direction of the global trend.",
    scalp: "Aggressive mode. Catching 'knives' and sharp movements. Uses tight stops."
  },
  signals: {
    title: "Signal Types (V2.0)",
    s_long: "Price hit Support Level + 'Hammer' pattern formed.",
    s_short: "Price hit Resistance Level + 'Shooting Star' pattern formed.",
    trend: "Trend entry on pullback to Bollinger Bands border (supported by EMA 200).",
    scalp: "Catching sharp bounce (Extreme RSI)."
  },
  algo: {
    title: "Decision Algorithm",
    s1_t: "1. Global Filter", s1_d: "Bot checks Bitcoin. If BTC volatility > 3% in 30 mins, trading stops.",
    s2_t: "2. Candidate Search", s2_d: "Scanner selects coins with volume > 3M USDT on the selected exchange.",
    s3_t: "3. TrendGuard™", s3_d: "🛑 CRITICAL: If Price < EMA 200, LONG is banned. If Price > EMA 200, SHORT is banned.",
    s4_t: "4. Analysis (V2)", s4_d: "Check RSI, levels, and candlestick patterns.",
    s5_t: "5. Execution", s5_d: "Risk calculation and order placement."
  },
  risk: {
    title: "Risk & Paper Trading",
    calc_title: "Position Calculation", calc_desc: "Bot calculates volume so that if Stop Loss is hit, you lose a fixed % of the deposit.",
    paper_desc: "In Paper Trading mode, the bot simulates Futures mechanics. Balance changes only when closing with PnL.",
    lev_desc: "Bot automatically selects leverage to not exceed risk.",
    trail_desc: "Stop Loss follows price with 1.5% step when profit > 2.5%."
  },
  logs: {
    title: "How to read logs?",
    l1: "Found smart entry (Level + Pattern).",
    l2: "TrendGuard triggered. Bot saved you from trading against the trend.",
    l3: "You switched trading platform.",
    l4: "Price went in our favor, stop moved to breakeven."
  },
  tg: {
    title: "Telegram Bot", cmd: "Command", desc: "Description",
    c1: "Start and welcome.", c2: "Link your account.", c3: "Current PnL and active trades.", c4: "Emergency trading stop."
  },
  trouble: {
    title: "Troubleshooting",
    p1_t: "'Insufficient Balance' Error", p1_d: "No USDT in your Futures wallet. Transfer funds from Spot to Futures.",
    p2_t: "Paper Balance glitch", p2_d: "Reset balance (🔄) in Profile to restore $10,000.",
    p3_t: "Bot not opening trades", p3_d: "TrendGuard filter triggered or Fear Index too low (<10).",
    p4_t: "Empty PnL chart", p4_d: "Chart is built only on CLOSED trades. Wait for the first profit.",
    p5_t: "'Timestamp...' Error", p5_d: "Time sync issue. Enable auto-time sync in Windows/Mac.",
    p6_t: "'Advisor Mode Active'", p6_d: "You enabled Advisor mode. Bot only sends signals. Uncheck in Profile."
  },
  support: { title: "Technical Support", desc: "Found a bug or have a strategy idea?", btn: "Contact Us" }
};

const guideRU = {
  nav: { title: "Содержание", intro: "Введение и Биржи", start: "Первый запуск", modes: "Режимы Стратегии", signals: "Типы Сигналов V2.0", logic: "Алгоритм TrendGuard", risk: "Риск и Paper Trading", logs: "Системные логи", telegram: "Telegram", trouble: "Решение проблем", faq: "FAQ" },
  header: { title: "База Знаний v1.5", desc: "Полная документация по Trading Bot Pro. Узнайте, как система зарабатывает на Binance и Bybit, как работает защита TrendGuard™ и как управлять рисками." },
  intro: { 
    title: "Что умеет этот бот?", 
    text: "Это автономная торговая система для рынка Фьючерсов. Она анализирует рынок 24/7 и находит точки входа, используя технический анализ (RSI, EMA, Bollinger) и Price Action.",
    f1: "Поддержка Binance Futures и Bybit Futures. Легкое переключение в настройках.",
    f2: "Новая система защиты (V6.4). Бот анализирует EMA 200 и запрещает открывать сделки против глобального тренда.",
    f3: "Вход только лимитными ордерами. Это экономит комиссию и защищает от проскальзывания.",
    f4_t: "Защита от Краха", f4_d: "Торговля блокируется при экстремальной волатильности Bitcoin или панике."
  },
  start: {
    title: "Быстрый старт",
    s1_t: "Настройка API Ключей", s1_l1: "Создайте ключи на Binance или Bybit.", s1_l2: "Включите 'Enable Futures'.", s1_l3: "Выключите доступ к выводу (Withdrawal).",
    s2_t: "Подключение", s2_d: "В меню 'Настройки' выберите биржу и введите ключи.",
    s3_t: "Режим торговли", s3_d: "Рекомендуем начать с Paper Trading (Симуляция на виртуальные $10,000)."
  },
  modes: {
    title: "Режимы Стратегии (Pro)", desc: "Вы можете переключать логику работы бота прямо из заголовка Дашборда.",
    auto: "Гибридный режим. Баланс между трендовыми сделками и отскоками. Рекомендуется для большинства.",
    trend: "Консервативный режим. Жесткий фильтр EMA 200. Бот торгует ТОЛЬКО по направлению глобального тренда.",
    scalp: "Агрессивный режим. Ловля 'ножей' и резких движений. Использует короткие стопы."
  },
  signals: {
    title: "Типы Сигналов (V2.0)",
    s_long: "Цена ударилась об Уровень Поддержки + Сформировался паттерн 'Молот'.",
    s_short: "Цена ударилась об Уровень Сопротивления + Сформировался паттерн 'Падающая Звезда'.",
    trend: "Вход по тренду на откате к границе Bollinger Bands (при поддержке EMA 200).",
    scalp: "Ловля резкого отскока (RSI экстремальный)."
  },
  algo: {
    title: "Алгоритм принятия решений",
    s1_t: "1. Глобальный фильтр", s1_d: "Бот проверяет Bitcoin. Если волатильность BTC > 3% за 30 минут, торговля останавливается.",
    s2_t: "2. Поиск кандидата", s2_d: "Сканер отбирает монеты с объемом > 3M USDT на выбранной бирже.",
    s3_t: "3. TrendGuard™", s3_d: "🛑 КРИТИЧЕСКИЙ ЭТАП: Если Цена < EMA 200, бот запрещает LONG. Если Цена > EMA 200, бот запрещает SHORT.",
    s4_t: "4. Анализ (V2)", s4_d: "Проверка RSI, уровней и свечных паттернов.",
    s5_t: "5. Исполнение", s5_d: "Расчет риска и открытие ордера."
  },
  risk: {
    title: "Риск и Paper Trading",
    calc_title: "Расчет позиции", calc_desc: "Бот рассчитывает объем так, чтобы при стоп-лоссе вы потеряли фиксированный % депозита.",
    paper_desc: "В режиме Paper Trading бот симулирует Фьючерсную механику. Баланс меняется только при закрытии на сумму PnL.",
    lev_desc: "Бот автоматически подбирает плечо, чтобы не превысить риск.",
    trail_desc: "Стоп-лосс следует за ценой с шагом 1.5% при профите > 2.5%."
  },
  logs: {
    title: "Как читать логи?",
    l1: "Найден умный вход (Уровень + Паттерн).",
    l2: "Сработала защита TrendGuard. Бот спас вас от входа против тренда.",
    l3: "Вы сменили торговую платформу.",
    l4: "Цена пошла в нашу сторону, стоп переставлен в безубыток."
  },
  tg: {
    title: "Telegram Бот", cmd: "Команда", desc: "Описание",
    c1: "Запуск и приветствие.", c2: "Привязка вашего аккаунта (код придет в профиль).", c3: "Текущий PnL и активные сделки.", c4: "Экстренная остановка торговли."
  },
  trouble: {
    title: "Решение частых проблем",
    p1_t: "Ошибка 'Insufficient Balance'", p1_d: "На вашем Фьючерсном кошельке нет USDT. Переведите средства со Спота.",
    p2_t: "Paper Balance сбился", p2_d: "Нажмите кнопку сброса (🔄) в Профиле, чтобы вернуть $10,000.",
    p3_t: "Бот не открывает сделки", p3_d: "Сработал фильтр TrendGuard (цена против тренда) или Индекс Страха слишком низкий (<10).",
    p4_t: "График PnL пустой", p4_d: "График строится только по ЗАКРЫТЫМ сделкам. Ждите первую прибыль.",
    p5_t: "Ошибка 'Timestamp...'", p5_d: "Рассинхронизация времени. Включите авто-синхронизацию времени в ОС.",
    p6_t: "'Advisor Mode Active'", p6_d: "Включен режим советника. Бот только шлет сигналы. Снимите галочку в Профиле."
  },
  support: { title: "Техническая поддержка", desc: "Нашли баг или есть идея для стратегии?", btn: "Написать нам" }
};

const guideDE = {
  nav: { title: "Inhaltsverzeichnis", intro: "Einführung & Börsen", start: "Schnellstart", modes: "Strategie-Modi", signals: "Signaltypen V2.0", logic: "TrendGuard Algo", risk: "Risiko & Paper Trading", logs: "Systemprotokolle", telegram: "Telegram", trouble: "Fehlerbehebung", faq: "FAQ" },
  header: { title: "Wissensdatenbank v1.5", desc: "Vollständige Dokumentation für Trading Bot Pro. Erfahren Sie, wie das System auf Binance und Bybit Gewinne erzielt, wie der TrendGuard™-Schutz funktioniert und wie Sie Risiken verwalten." },
  intro: { 
    title: "Was kann dieser Bot?", 
    text: "Dies ist ein autonomes Handelssystem für den Futures-Markt. Es analysiert den Markt 24/7 und findet Einstiegspunkte mittels technischer Analyse (RSI, EMA, Bollinger) und Price Action.",
    f1: "Unterstützung für Binance Futures und Bybit Futures. Einfaches Umschalten in den Einstellungen.",
    f2: "Neues Schutzsystem (V6.4). Der Bot analysiert den EMA 200 und verhindert Trades gegen den globalen Trend.",
    f3: "Einstieg nur über Limit-Orders. Das spart Gebühren und schützt vor Slippage.",
    f4_t: "Crash-Schutz", f4_d: "Der Handel wird bei extremer Bitcoin-Volatilität oder Marktpanik blockiert."
  },
  start: {
    title: "Schnellstart",
    s1_t: "API-Key Einrichtung", s1_l1: "Erstellen Sie Schlüssel auf Binance oder Bybit.", s1_l2: "Aktivieren Sie 'Enable Futures'.", s1_l3: "Deaktivieren Sie den Zugriff auf 'Withdrawal' (Auszahlung).",
    s2_t: "Verbindung", s2_d: "Wählen Sie in den 'Einstellungen' die Börse und geben Sie die Schlüssel ein.",
    s3_t: "Handelsmodus", s3_d: "Wir empfehlen den Start mit Paper Trading (Simulation mit virtuellen 10.000 $)."
  },
  modes: {
    title: "Strategie-Modi (Pro)", desc: "Sie können die Bot-Logik direkt im Dashboard-Header umschalten.",
    auto: "Hybrid-Modus. Balance zwischen Trend-Trades und Bounces. Für die meisten empfohlen.",
    trend: "Konservativer Modus. Harter EMA 200 Filter. Bot handelt NUR in Richtung des globalen Trends.",
    scalp: "Aggressiver Modus. Fängt 'fallende Messer' und scharfe Bewegungen. Nutzt enge Stops."
  },
  signals: {
    title: "Signaltypen (V2.0)",
    s_long: "Preis trifft Support Level + 'Hammer'-Muster gebildet.",
    s_short: "Preis trifft Widerstandslevel + 'Shooting Star'-Muster gebildet.",
    trend: "Trend-Einstieg bei Rücksetzer an die Bollinger-Band-Grenze (unterstützt durch EMA 200).",
    scalp: "Fangen eines scharfen Bounces (Extremer RSI)."
  },
  algo: {
    title: "Entscheidungsalgorithmus",
    s1_t: "1. Globaler Filter", s1_d: "Bot prüft Bitcoin. Wenn BTC-Volatilität > 3% in 30 Min., stoppt der Handel.",
    s2_t: "2. Kandidatensuche", s2_d: "Scanner wählt Coins mit Volumen > 3M USDT an der gewählten Börse.",
    s3_t: "3. TrendGuard™ (EMA 200)", s3_d: "🛑 KRITISCH: Wenn Preis < EMA 200, ist LONG verboten. Wenn Preis > EMA 200, ist SHORT verboten.",
    s4_t: "4. Analyse (V2)", s4_d: "Prüfung von RSI, Levels und Kerzenmustern.",
    s5_t: "5. Ausführung", s5_d: "Risikoberechnung und Orderplatzierung."
  },
  risk: {
    title: "Risiko & Paper Trading",
    calc_title: "Positionsberechnung", calc_desc: "Der Bot berechnet das Volumen so, dass Sie bei einem Stop Loss einen festen % des Depots verlieren.",
    paper_desc: "Im Paper Trading-Modus simuliert der Bot die Futures-Mechanik. Der Saldo ändert sich nur beim Schließen mit PnL.",
    lev_desc: "Der Bot wählt automatisch den Hebel, um das Risiko nicht zu überschreiten.",
    trail_desc: "Stop Loss folgt dem Preis mit 1,5% Schritt, wenn der Gewinn > 2,5% ist."
  },
  logs: {
    title: "Wie liest man Logs?",
    l1: "Smart Entry gefunden (Level + Muster).",
    l2: "TrendGuard ausgelöst. Bot hat Sie vor einem Trade gegen den Trend bewahrt.",
    l3: "Sie haben die Handelsplattform gewechselt.",
    l4: "Preis lief für uns, Stop auf Breakeven gesetzt."
  },
  tg: {
    title: "Telegram Bot", cmd: "Befehl", desc: "Beschreibung",
    c1: "Start und Begrüßung.", c2: "Konto verknüpfen (Code kommt im Profil).", c3: "Aktueller PnL und aktive Trades.", c4: "Not-Halt des Handels."
  },
  trouble: {
    title: "Fehlerbehebung",
    p1_t: "Fehler 'Insufficient Balance'", p1_d: "Kein USDT in Ihrer Futures-Wallet. Überweisen Sie Geld von Spot auf Futures.",
    p2_t: "Paper Balance Fehler", p2_d: "Drücken Sie Reset (🔄) im Profil, um 10.000 $ wiederherzustellen.",
    p3_t: "Bot öffnet keine Trades", p3_d: "TrendGuard-Filter ausgelöst oder Fear Index zu niedrig (<10).",
    p4_t: "Leerer PnL-Chart", p4_d: "Der Chart wird nur aus GESCHLOSSENEN Trades erstellt. Warten Sie auf den ersten Profit.",
    p5_t: "Fehler 'Timestamp...'", p5_d: "Zeitsynchronisierungsfehler. Aktivieren Sie die automatische Zeitsynchronisierung im OS.",
    p6_t: "'Advisor Mode Active'", p6_d: "Sie haben den Advisor-Modus aktiviert. Bot sendet nur Signale. Deaktivieren Sie dies im Profil."
  },
  support: { title: "Technischer Support", desc: "Bug gefunden oder eine Strategie-Idee?", btn: "Kontaktieren Sie uns" }
};

const guideES = {
  nav: { title: "Índice", intro: "Intro y Exchanges", start: "Inicio Rápido", modes: "Modos de Estrategia", signals: "Tipos de Señales V2.0", logic: "Algo TrendGuard", risk: "Riesgo y Paper Trading", logs: "Logs del Sistema", telegram: "Telegram", trouble: "Solución de Problemas", faq: "FAQ" },
  header: { title: "Base de Conocimientos v1.5", desc: "Documentación completa para Trading Bot Pro. Aprende cómo el sistema gana en Binance y Bybit, cómo funciona la protección TrendGuard™ y cómo gestionar riesgos." },
  intro: { 
    title: "¿Qué puede hacer este bot?", 
    text: "Es un sistema de trading autónomo para el mercado de Futuros. Analiza el mercado 24/7 y encuentra puntos de entrada usando análisis técnico (RSI, EMA, Bollinger) y Price Action.",
    f1: "Soporte para Binance Futures y Bybit Futures. Cambio fácil en la configuración.",
    f2: "Nuevo sistema de protección (V6.4). El bot analiza la EMA 200 y evita operaciones contra la tendencia global.",
    f3: "Entrada solo mediante órdenes límite. Ahorra comisiones y protege contra el deslizamiento.",
    f4_t: "Protección contra Crash", f4_d: "El trading se bloquea durante volatilidad extrema de Bitcoin o pánico."
  },
  start: {
    title: "Inicio Rápido",
    s1_t: "Configuración API Keys", s1_l1: "Crea claves en Binance o Bybit.", s1_l2: "Activa 'Enable Futures'.", s1_l3: "Desactiva el acceso a 'Withdrawal' (Retiros).",
    s2_t: "Conexión", s2_d: "En 'Ajustes', selecciona el exchange e introduce las claves.",
    s3_t: "Modo de Trading", s3_d: "Recomendamos comenzar con Paper Trading (Simulación con $10,000 virtuales)."
  },
  modes: {
    title: "Modos de Estrategia (Pro)", desc: "Puedes cambiar la lógica del bot directamente desde el encabezado del Dashboard.",
    auto: "Modo híbrido. Equilibrio entre operaciones de tendencia y rebotes. Recomendado para la mayoría.",
    trend: "Modo conservador. Filtro EMA 200 estricto. El bot opera SOLO en la dirección de la tendencia global.",
    scalp: "Modo agresivo. Atrapa 'cuchillos cayendo' y movimientos bruscos. Usa stops ajustados."
  },
  signals: {
    title: "Tipos de Señales (V2.0)",
    s_long: "Precio toca Soporte + Patrón 'Martillo' formado.",
    s_short: "Precio toca Resistencia + Patrón 'Estrella Fugaz' formado.",
    trend: "Entrada de tendencia en retroceso al borde de Bollinger (apoyado por EMA 200).",
    scalp: "Atrapando rebote brusco (RSI Extremo)."
  },
  algo: {
    title: "Algoritmo de Decisión",
    s1_t: "1. Filtro Global", s1_d: "El bot verifica Bitcoin. Si la volatilidad de BTC > 3% en 30 min, el trading se detiene.",
    s2_t: "2. Búsqueda de Candidatos", s2_d: "El escáner selecciona monedas con volumen > 3M USDT en el exchange seleccionado.",
    s3_t: "3. TrendGuard™ (EMA 200)", s3_d: "🛑 CRÍTICO: Si Precio < EMA 200, LONG está prohibido. Si Precio > EMA 200, SHORT está prohibido.",
    s4_t: "4. Análisis (V2)", s4_d: "Verificación de RSI, niveles y patrones de velas.",
    s5_t: "5. Ejecución", s5_d: "Cálculo de riesgo y colocación de orden."
  },
  risk: {
    title: "Riesgo y Paper Trading",
    calc_title: "Cálculo de Posición", calc_desc: "El bot calcula el volumen para que si toca el Stop Loss, pierdas un % fijo del depósito.",
    paper_desc: "En modo Paper Trading, el bot simula la mecánica de Futuros. El saldo cambia solo al cerrar con PnL.",
    lev_desc: "El bot selecciona automáticamente el apalancamiento para no exceder el riesgo.",
    trail_desc: "Stop Loss sigue al precio con paso de 1.5% cuando la ganancia > 2.5%."
  },
  logs: {
    title: "¿Cómo leer los logs?",
    l1: "Entrada inteligente encontrada (Nivel + Patrón).",
    l2: "TrendGuard activado. El bot te salvó de operar contra la tendencia.",
    l3: "Cambiaste de plataforma de trading.",
    l4: "El precio fue a nuestro favor, stop movido a breakeven."
  },
  tg: {
    title: "Bot de Telegram", cmd: "Comando", desc: "Descripción",
    c1: "Inicio y bienvenida.", c2: "Vincular cuenta (el código llegará al perfil).", c3: "PnL actual y operaciones activas.", c4: "Parada de emergencia del trading."
  },
  trouble: {
    title: "Solución de Problemas",
    p1_t: "Error 'Insufficient Balance'", p1_d: "No hay USDT en tu billetera de Futuros. Transfiere fondos de Spot a Futuros.",
    p2_t: "Fallo en Saldo Paper", p2_d: "Presiona Reset (🔄) en Perfil para restaurar $10,000.",
    p3_t: "El bot no abre operaciones", p3_d: "Filtro TrendGuard activado o Índice de Miedo demasiado bajo (<10).",
    p4_t: "Gráfico PnL vacío", p4_d: "El gráfico se construye solo con operaciones CERRADAS. Espera la primera ganancia.",
    p5_t: "Error 'Timestamp...'", p5_d: "Problema de sincronización de hora. Activa la sincronización automática en tu SO.",
    p6_t: "'Advisor Mode Active'", p6_d: "Activaste modo consejero. El bot solo envía señales. Desactívalo en Perfil."
  },
  support: { title: "Soporte Técnico", desc: "¿Encontraste un bug o tienes una idea?", btn: "Contáctanos" }
};

const guideFR = {
  nav: { title: "Sommaire", intro: "Intro & Bourses", start: "Démarrage Rapide", modes: "Modes Stratégie", signals: "Types de Signaux V2.0", logic: "Algo TrendGuard", risk: "Risque & Paper Trading", logs: "Logs Système", telegram: "Telegram", trouble: "Dépannage", faq: "FAQ" },
  header: { title: "Base de Connaissances v1.5", desc: "Documentation complète pour Trading Bot Pro. Apprenez comment le système gagne sur Binance et Bybit, comment fonctionne la protection TrendGuard™ et comment gérer les risques." },
  intro: { 
    title: "Que peut faire ce bot ?", 
    text: "C'est un système de trading autonome pour le marché des Futures. Il analyse le marché 24/7 et trouve des points d'entrée via analyse technique (RSI, EMA, Bollinger) et Price Action.",
    f1: "Support pour Binance Futures et Bybit Futures. Changement facile dans les paramètres.",
    f2: "Nouveau système de protection (V6.4). Le bot analyse l'EMA 200 et empêche les trades contre la tendance globale.",
    f3: "Entrée uniquement par ordres limit. Économise les frais et protège contre le slippage.",
    f4_t: "Protection Crash", f4_d: "Le trading est bloqué lors de volatilité extrême du Bitcoin ou panique."
  },
  start: {
    title: "Démarrage Rapide",
    s1_t: "Config Clés API", s1_l1: "Créez des clés sur Binance ou Bybit.", s1_l2: "Activez 'Enable Futures'.", s1_l3: "Désactivez l'accès 'Withdrawal' (Retrait).",
    s2_t: "Connexion", s2_d: "Dans 'Paramètres', choisissez l'échange et entrez les clés.",
    s3_t: "Mode de Trading", s3_d: "Nous recommandons de commencer par le Paper Trading (Simulation avec 10 000 $ virtuels)."
  },
  modes: {
    title: "Modes de Stratégie (Pro)", desc: "Vous pouvez changer la logique du bot directement depuis l'en-tête du Dashboard.",
    auto: "Mode hybride. Équilibre entre trades de tendance et rebonds. Recommandé pour la plupart.",
    trend: "Mode conservateur. Filtre EMA 200 strict. Le bot trade UNIQUEMENT dans le sens de la tendance globale.",
    scalp: "Mode agressif. Capture les 'couteaux qui tombent' et mouvements brusques. Stops serrés."
  },
  signals: {
    title: "Types de Signaux (V2.0)",
    s_long: "Prix touche Support + Pattern 'Marteau' formé.",
    s_short: "Prix touche Résistance + Pattern 'Étoile Filante' formé.",
    trend: "Entrée tendance sur repli vers la bande de Bollinger (soutenu par EMA 200).",
    scalp: "Capture de rebond brusque (RSI Extrême)."
  },
  algo: {
    title: "Algorithme de Décision",
    s1_t: "1. Filtre Global", s1_d: "Le bot vérifie Bitcoin. Si volatilité BTC > 3% en 30 min, le trading s'arrête.",
    s2_t: "2. Recherche Candidats", s2_d: "Le scanner sélectionne les pièces avec volume > 3M USDT sur l'échange choisi.",
    s3_t: "3. TrendGuard™ (EMA 200)", s3_d: "🛑 CRITIQUE : Si Prix < EMA 200, LONG interdit. Si Prix > EMA 200, SHORT interdit.",
    s4_t: "4. Analyse (V2)", s4_d: "Vérification RSI, niveaux et patterns de chandeliers.",
    s5_t: "5. Exécution", s5_d: "Calcul du risque et placement de l'ordre."
  },
  risk: {
    title: "Risque & Paper Trading",
    calc_title: "Calcul de Position", calc_desc: "Le bot calcule le volume pour que si le Stop Loss est touché, vous perdiez un % fixe du dépôt.",
    paper_desc: "En mode Paper Trading, le bot simule la mécanique Futures. Le solde ne change qu'à la clôture avec PnL.",
    lev_desc: "Le bot sélectionne automatiquement le levier pour ne pas dépasser le risque.",
    trail_desc: "Stop Loss suit le prix avec un pas de 1.5% quand le profit > 2.5%."
  },
  logs: {
    title: "Comment lire les logs ?",
    l1: "Entrée intelligente trouvée (Niveau + Pattern).",
    l2: "TrendGuard déclenché. Le bot vous a sauvé d'un trade contre la tendance.",
    l3: "Vous avez changé de plateforme de trading.",
    l4: "Le prix est allé en notre faveur, stop déplacé à breakeven."
  },
  tg: {
    title: "Bot Telegram", cmd: "Commande", desc: "Description",
    c1: "Démarrage et bienvenue.", c2: "Lier votre compte (le code arrivera dans le profil).", c3: "PnL actuel et trades actifs.", c4: "Arrêt d'urgence du trading."
  },
  trouble: {
    title: "Dépannage",
    p1_t: "Erreur 'Insufficient Balance'", p1_d: "Pas d'USDT sur votre portefeuille Futures. Transférez depuis Spot.",
    p2_t: "Bug Solde Paper", p2_d: "Appuyez sur Reset (🔄) dans Profil pour restaurer 10 000 $.",
    p3_t: "Le bot n'ouvre pas de trades", p3_d: "Filtre TrendGuard déclenché ou Index de Peur trop bas (<10).",
    p4_t: "Graphique PnL vide", p4_d: "Le graphique se construit sur les trades CLÔTURÉS. Attendez le premier profit.",
    p5_t: "Erreur 'Timestamp...'", p5_d: "Problème de sync heure. Activez la sync auto dans l'OS.",
    p6_t: "'Advisor Mode Active'", p6_d: "Mode conseiller activé. Le bot envoie juste des signaux. Décochez dans Profil."
  },
  support: { title: "Support Technique", desc: "Trouvé un bug ou une idée ?", btn: "Nous contacter" }
};

const guideZH = {
  nav: { title: "目录", intro: "简介与交易所", start: "快速开始", modes: "策略模式", signals: "信号类型 V2.0", logic: "TrendGuard 算法", risk: "风险与模拟交易", logs: "系统日志", telegram: "Telegram", trouble: "故障排除", faq: "常见问题" },
  header: { title: "知识库 v1.5", desc: "Trading Bot Pro 完整文档。了解系统如何在 Binance 和 Bybit 上盈利，TrendGuard™ 保护如何工作以及如何管理风险。" },
  intro: { 
    title: "这个机器人能做什么？", 
    text: "这是一个用于期货市场的自主交易系统。它全天候分析市场，利用技术分析（RSI、EMA、布林带）和价格行为寻找入场点。",
    f1: "支持币安合约和 Bybit 合约。在设置中轻松切换。",
    f2: "新保护系统 (V6.4)。机器人分析 EMA 200 并防止逆势开单。",
    f3: "仅通过限价单入场。节省手续费并防止滑点。",
    f4_t: "崩溃保护", f4_d: "在比特币极端波动或市场恐慌期间暂停交易。"
  },
  start: {
    title: "快速开始",
    s1_t: "API 密钥设置", s1_l1: "在币安或 Bybit 创建密钥。", s1_l2: "启用 '允许合约'。", s1_l3: "禁用 '提现' 权限。",
    s2_t: "连接", s2_d: "在 '设置' 中选择交易所并输入密钥。",
    s3_t: "交易模式", s3_d: "我们建议从模拟交易开始（拥有 10,000 美元虚拟资金）。"
  },
  modes: {
    title: "策略模式 (Pro)", desc: "您可以直接从仪表板标题切换机器人逻辑。",
    auto: "混合模式。趋势交易和反弹之间的平衡。推荐给大多数人。",
    trend: "保守模式。严格的 EMA 200 过滤。机器人仅顺应全球趋势交易。",
    scalp: "激进模式。捕捉 '飞刀' 和急剧波动。使用紧密止损。"
  },
  signals: {
    title: "信号类型 (V2.0)",
    s_long: "价格触及支撑位 + 形成 '锤子线' 形态。",
    s_short: "价格触及阻力位 + 形成 '射击之星' 形态。",
    trend: "趋势入场：回调至布林带边缘（由 EMA 200 支持）。",
    scalp: "捕捉急剧反弹（极端 RSI）。"
  },
  algo: {
    title: "决策算法",
    s1_t: "1. 全局过滤", s1_d: "机器人检查比特币。如果 BTC 30分钟内波动 > 3%，停止交易。",
    s2_t: "2. 候选搜索", s2_d: "扫描器在所选交易所筛选成交量 > 300万 USDT 的币种。",
    s3_t: "3. TrendGuard™ (EMA 200)", s3_d: "🛑 关键：如果价格 < EMA 200，禁止做多。如果价格 > EMA 200，禁止做空。",
    s4_t: "4. 分析 (V2)", s4_d: "检查 RSI、水平位和K线形态。",
    s5_t: "5. 执行", s5_d: "风险计算和下单。"
  },
  risk: {
    title: "风险与模拟交易",
    calc_title: "仓位计算", calc_desc: "机器人计算仓位，以便在触发止损时，您只损失固定百分比的本金。",
    paper_desc: "在模拟交易模式下，机器人模拟期货机制。余额仅在平仓产生盈亏时变化。",
    lev_desc: "机器人自动选择杠杆以不超过风险。",
    trail_desc: "当利润 > 2.5% 时，止损跟随价格以 1.5% 的步长移动。"
  },
  logs: {
    title: "如何阅读日志？",
    l1: "发现智能入场（水平 + 形态）。",
    l2: "TrendGuard 触发。机器人阻止了逆势交易。",
    l3: "您切换了交易平台。",
    l4: "价格对我们有利，止损移动至保本。"
  },
  tg: {
    title: "Telegram 机器人", cmd: "命令", desc: "描述",
    c1: "开始和欢迎。", c2: "绑定账户（代码将在个人资料中显示）。", c3: "当前盈亏和活跃交易。", c4: "紧急停止交易。"
  },
  trouble: {
    title: "故障排除",
    p1_t: "错误 'Insufficient Balance'", p1_d: "您的合约钱包中没有 USDT。请从现货划转资金。",
    p2_t: "模拟余额异常", p2_d: "在个人资料中按重置 (🔄) 以恢复 10,000 美元。",
    p3_t: "机器人不开单", p3_d: "TrendGuard 过滤器触发（逆势）或恐慌指数太低 (<10)。",
    p4_t: "盈亏图表为空", p4_d: "图表仅基于已平仓交易构建。等待第一笔盈利。",
    p5_t: "错误 'Timestamp...'", p5_d: "时间同步问题。在操作系统中启用自动时间同步。",
    p6_t: "'Advisor Mode Active'", p6_d: "您启用了顾问模式。机器人只发送信号。在个人资料中取消勾选。"
  },
  support: { title: "技术支持", desc: "发现错误或有策略想法？", btn: "联系我们" }
};

const guideJA = {
  nav: { title: "目次", intro: "はじめに & 取引所", start: "クイックスタート", modes: "戦略モード", signals: "シグナルタイプ V2.0", logic: "TrendGuard アルゴ", risk: "リスク & デモトレード", logs: "システムログ", telegram: "Telegram", trouble: "トラブルシューティング", faq: "FAQ" },
  header: { title: "ナレッジベース v1.5", desc: "Trading Bot Pro の完全ドキュメント。Binance と Bybit での収益方法、TrendGuard™ 保護の仕組み、リスク管理について学びましょう。" },
  intro: { 
    title: "このボットは何ができますか？", 
    text: "これは先物市場向けの自律型取引システムです。24時間365日市場を分析し、テクニカル分析（RSI、EMA、ボリンジャー）とプライスアクションを使用してエントリーポイントを見つけます。",
    f1: "Binance Futures と Bybit Futures に対応。設定で簡単に切り替え可能。",
    f2: "新しい保護システム (V6.4)。ボットは EMA 200 を分析し、グローバルトレンドに逆らう取引を防ぎます。",
    f3: "指値注文のみでエントリー。手数料を節約し、スリッページを防ぎます。",
    f4_t: "クラッシュ保護", f4_d: "ビットコインの極端な変動やパニック時には取引がブロックされます。"
  },
  start: {
    title: "クイックスタート",
    s1_t: "APIキー設定", s1_l1: "Binance または Bybit でキーを作成。", s1_l2: "'先物取引を有効にする' をオン。", s1_l3: "'出金' アクセスをオフ。",
    s2_t: "接続", s2_d: "'設定' で取引所を選択し、キーを入力。",
    s3_t: "取引モード", s3_d: "ペーパートレーディング（仮想 $10,000 でのシミュレーション）から始めることをお勧めします。"
  },
  modes: {
    title: "戦略モード (Pro)", desc: "ダッシュボードヘッダーからボットのロジックを直接切り替えることができます。",
    auto: "ハイブリッドモード。トレンド取引と反発のバランス。ほとんどの方に推奨。",
    trend: "保守モード。厳格な EMA 200 フィルタ。ボットはグローバルトレンドの方向にのみ取引します。",
    scalp: "攻撃的モード。急激な動きや '落ちるナイフ' を捉えます。タイトなストップを使用。",
  },
  signals: {
    title: "シグナルタイプ (V2.0)",
    s_long: "価格がサポートに到達 + 'ハンマー' パターン形成。",
    s_short: "価格がレジスタンスに到達 + 'シューティングスター' パターン形成。",
    trend: "ボリンジャーバンド境界への押し目でのトレンドエントリー（EMA 200 でサポート）。",
    scalp: "急激な反発を捉える（極端な RSI）。"
  },
  algo: {
    title: "意思決定アルゴリズム",
    s1_t: "1. グローバルフィルタ", s1_d: "ボットはビットコインをチェックします。BTC変動率が30分で3%を超えると、取引停止。",
    s2_t: "2. 候補検索", s2_d: "スキャナーは、選択された取引所で出来高 > 3M USDT のコインを選択。",
    s3_t: "3. TrendGuard™ (EMA 200)", s3_d: "🛑 重要: 価格 < EMA 200 の場合、LONG 禁止。価格 > EMA 200 の場合、SHORT 禁止。",
    s4_t: "4. 分析 (V2)", s4_d: "RSI、レベル、ローソク足パターンの確認。",
    s5_t: "5. 実行", s5_d: "リスク計算と注文配置。"
  },
  risk: {
    title: "リスク & デモトレード",
    calc_title: "ポジション計算", calc_desc: "ストップロスに達した場合、預金の固定 % を失うようにボリュームを計算します。",
    paper_desc: "ペーパートレーディングモードでは、先物の仕組みをシミュレートします。残高は PnL で決済した時のみ変動します。",
    lev_desc: "リスクを超えないように、ボットが自動的にレバレッジを選択します。",
    trail_desc: "利益 > 2.5% の場合、ストップロスは 1.5% のステップで価格に追従します。"
  },
  logs: {
    title: "ログの読み方",
    l1: "スマートエントリー発見（レベル + パターン）。",
    l2: "TrendGuard 発動。トレンドに逆らう取引を防ぎました。",
    l3: "取引プラットフォームを切り替えました。",
    l4: "価格が有利に動き、ストップを建値に移動。"
  },
  tg: {
    title: "Telegram ボット", cmd: "コマンド", desc: "説明",
    c1: "開始と挨拶。", c2: "アカウント連携（コードはプロフィールに届きます）。", c3: "現在の PnL とアクティブな取引。", c4: "緊急取引停止。"
  },
  trouble: {
    title: "トラブルシューティング",
    p1_t: "エラー 'Insufficient Balance'", p1_d: "先物ウォレットに USDT がありません。現物から振替してください。",
    p2_t: "デモ残高の不具合", p2_d: "プロフィールのリセット (🔄) を押して $10,000 に戻してください。",
    p3_t: "ボットが取引しない", p3_d: "TrendGuard フィルタ発動（逆トレンド）または恐怖指数が低すぎる (<10)。",
    p4_t: "PnL チャートが空", p4_d: "チャートは決済済みの取引のみで構築されます。最初の利益を待ってください。",
    p5_t: "エラー 'Timestamp...'", p5_d: "時間の同期問題。OS で自動時刻同期を有効にしてください。",
    p6_t: "'Advisor Mode Active'", p6_d: "アドバイザーモードが有効です。シグナルのみ送信します。プロフィールでオフにしてください。"
  },
  support: { title: "テクニカルサポート", desc: "バグ発見やアイデアがありますか？", btn: "お問い合わせ" }
};

const guideKO = {
  nav: { title: "목차", intro: "소개 및 거래소", start: "빠른 시작", modes: "전략 모드", signals: "신호 유형 V2.0", logic: "TrendGuard 알고리즘", risk: "리스크 및 모의 투자", logs: "시스템 로그", telegram: "텔레그램", trouble: "문제 해결", faq: "FAQ" },
  header: { title: "지식 베이스 v1.5", desc: "Trading Bot Pro 전체 문서. Binance 및 Bybit 수익 창출 방법, TrendGuard™ 보호 작동 방식, 리스크 관리 방법을 알아보세요." },
  intro: { 
    title: "이 봇은 무엇을 할 수 있나요?", 
    text: "이것은 선물 시장을 위한 자율 거래 시스템입니다. 연중무휴로 시장을 분석하고 기술적 분석(RSI, EMA, 볼린저) 및 프라이스 액션을 사용하여 진입점을 찾습니다.",
    f1: "Binance Futures 및 Bybit Futures 지원. 설정에서 쉽게 전환 가능.",
    f2: "새로운 보호 시스템 (V6.4). 봇은 EMA 200을 분석하여 글로벌 추세에 반하는 거래를 방지합니다.",
    f3: "지정가 주문으로만 진입. 수수료를 절약하고 슬리피지를 방지합니다.",
    f4_t: "급락 보호", f4_d: "비트코인 변동성이 극심하거나 패닉 상태일 때 거래가 차단됩니다."
  },
  start: {
    title: "빠른 시작",
    s1_t: "API 키 설정", s1_l1: "Binance 또는 Bybit에서 키 생성.", s1_l2: "'Enable Futures' 활성화.", s1_l3: "'Withdrawal'(출금) 권한 비활성화.",
    s2_t: "연결", s2_d: "'설정'에서 거래소를 선택하고 키를 입력하세요.",
    s3_t: "거래 모드", s3_d: "Paper Trading(가상 $10,000 시뮬레이션)으로 시작하는 것을 권장합니다."
  },
  modes: {
    title: "전략 모드 (Pro)", desc: "대시보드 헤더에서 봇 로직을 직접 전환할 수 있습니다.",
    auto: "하이브리드 모드. 추세 거래와 반등 사이의 균형. 대부분의 사용자에게 권장.",
    trend: "보수적 모드. 엄격한 EMA 200 필터. 봇은 글로벌 추세 방향으로만 거래합니다.",
    scalp: "공격적 모드. 급격한 움직임을 포착합니다. 타이트한 스톱 사용."
  },
  signals: {
    title: "신호 유형 (V2.0)",
    s_long: "가격이 지지선 도달 + '망치형' 패턴 형성.",
    s_short: "가격이 저항선 도달 + '유성형' 패턴 형성.",
    trend: "볼린저 밴드 경계로 되돌림 시 추세 진입 (EMA 200 지지).",
    scalp: "급격한 반등 포착 (RSI 과매도/과매수)."
  },
  algo: {
    title: "의사 결정 알고리즘",
    s1_t: "1. 글로벌 필터", s1_d: "봇이 비트코인을 확인합니다. 30분 내 BTC 변동성 > 3% 시 거래 중단.",
    s2_t: "2. 후보 검색", s2_d: "스캐너가 선택한 거래소에서 거래량 > 300만 USDT 코인 선별.",
    s3_t: "3. TrendGuard™ (EMA 200)", s3_d: "🛑 중요: 가격 < EMA 200이면 롱 금지. 가격 > EMA 200이면 숏 금지.",
    s4_t: "4. 분석 (V2)", s4_d: "RSI, 레벨 및 캔들스틱 패턴 확인.",
    s5_t: "5. 실행", s5_d: "리스크 계산 및 주문 배치."
  },
  risk: {
    title: "리스크 및 모의 투자",
    calc_title: "포지션 계산", calc_desc: "봇은 손절매가 발생할 경우 예치금의 고정 %를 잃도록 거래량을 계산합니다.",
    paper_desc: "Paper Trading 모드에서는 선물 메커니즘을 시뮬레이션합니다. 잔액은 PnL로 청산할 때만 변경됩니다.",
    lev_desc: "봇은 리스크를 초과하지 않도록 레버리지를 자동으로 선택합니다.",
    trail_desc: "수익 > 2.5%일 때 스톱로스는 1.5% 간격으로 가격을 따라갑니다."
  },
  logs: {
    title: "로그 읽는 법",
    l1: "스마트 진입 발견 (레벨 + 패턴).",
    l2: "TrendGuard 작동. 추세에 반하는 거래로부터 보호했습니다.",
    l3: "거래 플랫폼을 전환했습니다.",
    l4: "가격이 유리하게 이동하여 스톱을 본절로 이동."
  },
  tg: {
    title: "텔레그램 봇", cmd: "명령어", desc: "설명",
    c1: "시작 및 환영.", c2: "계정 연동 (코드는 프로필에 도착).", c3: "현재 PnL 및 활성 거래.", c4: "긴급 거래 중지."
  },
  trouble: {
    title: "문제 해결",
    p1_t: "'Insufficient Balance' 오류", p1_d: "선물 지갑에 USDT가 없습니다. 현물에서 이체하세요.",
    p2_t: "모의 잔액 오류", p2_d: "프로필에서 초기화(🔄)를 눌러 $10,000를 복구하세요.",
    p3_t: "봇이 거래를 안 함", p3_d: "TrendGuard 필터 작동(역추세) 또는 공포 지수 너무 낮음(<10).",
    p4_t: "PnL 차트 비어있음", p4_d: "차트는 청산된 거래로만 생성됩니다. 첫 수익을 기다리세요.",
    p5_t: "'Timestamp...' 오류", p5_d: "시간 동기화 문제. OS에서 자동 시간 동기화를 켜세요.",
    p6_t: "'Advisor Mode Active'", p6_d: "조언자 모드가 켜져 있습니다. 신호만 보냅니다. 프로필에서 체크 해제하세요."
  },
  support: { title: "기술 지원", desc: "버그를 발견했거나 전략 아이디어가 있나요?", btn: "문의하기" }
};

// ============================================================
// 🌍 MAIN RESOURCES
// ============================================================

const resources = {
  // --- ENGLISH ---
  en: {
    translation: {
      landing: {
        nav: { howItWorks: "How it works", features: "Features", pricing: "Pricing", faq: "FAQ", login: "Login", start: "Start Free", terminal: "Terminal", settings: "Settings", billing: "Subscription & Billing" },
        hero: { badge: "V6.4: TrendGuard™ AI Activated", title: "Trade Crypto like an", subtitle: "Algorithmic Fund", desc: "Connect Binance or Bybit in 2 minutes. Protection against drains, trend analysis and auto-risk management.", cta: "Start Bot", demo: "Live Demo" },
        stats: { volume: "Trading Volume", traders: "Active Traders", uptime: "Uptime", speed: "Execution Speed" },
        how: { title: "How to start earning?", subtitle: "You don't need to be a programmer or trader. We did everything for you.", step1: { title: "Create Account", desc: "Registration takes 30 seconds. You immediately get $10,000 on a demo account for testing." }, step2: { title: "Connect Exchange", desc: "Enter API keys from Binance or Bybit. We do not have access to Withdrawal, only trading." }, step3: { title: "Select Strategy", desc: "Enable 'Trend' mode for conservative trading or 'Scalp' for aggressive trading." } },
        fake: { mode: "TRADING MODE", risk: "RISK PER TRADE" },
        features: { title: "Wall Street Tech at Home", subtitle: "We combined 3 powerful algorithms in one platform.", c1: { title: "Multi-Exchange", desc: "Trade simultaneously on Binance Futures and Bybit. Single interface to manage all accounts without switching." }, c2: { title: "TrendGuard™ Protection", desc: "Smart filter based on EMA 200. The bot automatically bans buying in a falling market, saving the deposit from 'catching knives'." }, c3: { title: "Smart Entry V2", desc: "Sniper entry. The bot finds ideal points based on RSI, support levels, and candlestick patterns." } },
        pricing: { 
          title: "Transparent Pricing", 
          subtitle: "Start small or save up to 20% on an annual subscription.", 
          period: "/mo", 
          select: "Select Plan", 
          p1: { title: "Start", desc: "For beginners", f1: "1 Exchange", f2: "Basic Protection", f3: "Telegram Signals", f4: "Limit: $5,000" }, 
          p2: { title: "Trader", desc: "Most Popular", f1: "Binance + Bybit", f2: "TrendGuard™ Pro", f3: "Limit: $20,000", f4: "Scalp Mode" }, 
          p3: { title: "Pro", desc: "Maximum Power", f1: "All Exchanges", f2: "Unlimited Deposit", f3: "VIP Support", f4: "Early Access" }, 
          p4: { title: "Investor", desc: "Fully Automated", f1: "White Label", f2: "API Access", f3: "Personal Manager", f4: "Lifetime Discount" } 
        },
        contact: { title: "Questions left?", desc: "Not sure which plan to choose? Our support team responds within 15 minutes.", telegram: "Write on Telegram", email: "Write via Email" },
        faq: { title: "Frequently Asked Questions", subtitle: "Everything you wanted to know about security and bot operation.", q1: { q: "Is it safe to give API keys?", a: "Absolutely. When creating keys on Binance/Bybit, you check 'Enable Futures Trading' but DO NOT enable 'Withdrawal'. This means the bot can trade but cannot physically withdraw your money." }, q2: { q: "How much can I earn?", a: "The result depends on market volatility. On average, our users on the 'Trend' plan get 5-15% to the deposit per month." }, q3: { q: "What if the internet goes down?", a: "The bot runs on a cloud server 24/7. It doesn't matter if your computer is on or off." }, q4: { q: "Can I try for free?", a: "Yes! Upon registration, you get the FREE plan and $10,000 virtual funds." }, q5: { q: "What pairs does the bot trade?", a: "The bot scans top-50 coins by volume (BTC, ETH, SOL, XRP, etc.)." } },
        footer: { terms: "Terms of Service", privacy: "Privacy Policy", support: "Support" }
      },
      platform: {
        nav: { dashboard: "Overview", history: "History", reports: "Analytics", profile: "Profile", billing: "Subscription", system: "SYSTEM", settings: "API Keys", guide: "Guide", admin: "Admin", logout: "Logout" },
        risk: { title: "Risk Parameters", risk: "Risk", leverage: "Leverage", tp: "Take Profit", sl: "Stop Loss" },
        btc: { title: "BTC Market", safe: "SAFE", risk: "RISK" },
        actions: { sim: "Simulation (Paper)", real: "Real (Live)", start: "START SYSTEM", stop: "STOP SYSTEM", panic: "PANIC CLOSE", support: "Support Chat", active: "SYSTEM ACTIVE", paused: "SYSTEM PAUSED" },
        dashboard: { 
            title: "Analytics Terminal", 
            changeMode: "Change Mode", 
            live: "Live Data", 
            refresh: "Refresh", 
            updating: "Updating...", 
            paperTitleFree: "Simulation Mode (Free Plan)", 
            paperTitlePro: "Simulation Mode (Pro Active)", 
            paperDescFree: "You are trading with virtual funds. Upgrade for real trading.", 
            paperDescPro: "Using Paper Trading for testing. Switch to Real in sidebar.", 
            connectReal: "Connect Real Account", 
            virtualBal: "VIRTUAL BALANCE (PAPER)", 
            realBal: "REAL EXCHANGE BALANCE", 
            pnl: "Total PnL (24h)", 
            deals: "Active Deals", 
            winrate: "Win Rate", 
            chart: "Chart Analysis", 
            pnlDyn: "PnL Dynamics (7 Days)", 
            heatmap: "Market Heatmap" 
        },
        manualTrade: {
          title: "Manual Trading",
          symbol: "Trading Pair",
          margin: "Margin (USDT)",
          leverage: "Leverage (x)",
          positionSize: "Position Size:",
          long: "LONG",
          short: "SHORT",
          confirm: "Open {{type}} on {{symbol}} with margin ${{amount}}?",
          success: "Order placed successfully!",
          error: "Error: "
        },
        logs: { title: "System Live Logs", waiting: "Waiting for server events..." },
        ai: { title: "AI Analysis" },
        telegram: { title: "Telegram Management", subtitle: "Secure account linking", step1: "1. Open", step2: "2. Enter command:", codeLabel: "Verification Code", copy: "Copy command", waiting: "Waiting for Telegram request...", active: "Telegram Active", copyButton: "Copy", confirmationCodeLabel: "Confirmation Code" },
        apiSettings: { 
            title: "API Settings", 
            success: "Operation successful", 
            platform: "Platform Selection", 
            platformDesc: "Choose exchange to configure keys", 
            keysTitle: "Access Keys", 
            keysDesc: "Enter keys for", 
            apiKey: "API Key", 
            apiSecret: "Secret Key", 
            placeholderKey: "Paste API Key from", 
            placeholderSecret: "Paste Secret Key from", 
            placeholderSecretHidden: "Key hidden (enter new to replace)", 
            keySaved: "Key saved in system.", 
            saveBtn: "SAVE SETTINGS", 
            saving: "Saving...", 
            current: "Active Bot Exchange:", 
            currentDesc: "Bot is currently trading on:", 
            currentKey: "Key ends with:" 
        },
        footer: { docs: "Documentation", made: "Made with" },
        errors: { chartReload: "Widget reloading...", refresh: "Refresh" },
        history: {
            colAsset: "Asset / Strategy",
            colDetails: "Entry Details",
            colVolume: "Volume (USDT)",
            colPnl: "PnL & ROI",
            colAction: "Actions",
            qty: "Qty",
            trading: "Trading",
            filled: "Filled",
            close: "Close at Market",
            closing: "Closing...",
            emptyTitle: "Trading history is empty",
            legendSim: "Simulation",
            legendReal: "Real Account",
            totalOps: "Total operations:",
            confirmClose: "Close {{symbol}} position at market?",
            closeSuccess: "✅ Position {{symbol}} closed manually",
            error: "Error: ",
            logs: { title: "System Logs",waiting: "Waiting for events..."}
          },
          strategy: {
            advancedTitle: "Advanced Strategy",
            dcaTitle: "DCA (Martingale)",
            dcaDesc: "Averaging a position during a drawdown. The bot buys more to lower the average entry price.",
            dcaStep: "Step (%)",
            dcaMult: "Multiplier",
            dcaMax: "Max Orders",
            trailingTitle: "Trailing Take Profit",
            trailingDesc: "Allows profits to grow by following the price. Stop Loss moves up as the price rises.",
            trailingAct: "Activation (%)",
            trailingCall: "Callback (%)"
          },
          riskInfo: {
            balanceUsageDesc: "Bot will use no more than the specified percentage of the total deposit.",
            riskDesc: "Percentage of balance risked on a single trade.",
            leverageDesc: "Credit leverage for futures trading.",
            concurrentDesc: "Maximum number of simultaneously open positions.",
            dailyLossDesc: "Daily loss limit. Upon reaching it, the bot will stop trading until the next day.",
            minVolumeDesc: "Filter for coins by minimum daily trading volume in USDT."
          },

          reports: {
          title: "Trading Analytics",
          desc: "Deep analysis of your trading strategy performance",
          refresh: "Refresh Data",
          loading: "Analyzing Trades...",
          profit: "Total Net Profit",
          winrate: "Win Rate",
          totalTrades: "Total Trades",
          activeBans: "Active Bans",
          chartTitle: "Growth Curve (Daily PnL)",
          performanceTitle: "Asset Performance",
          tradesCount: "{{count}} trades",
          blacklistTitle: "Restricted Assets (Blacklist)",
          bannedUntil: "Banned until",
          noBans: "No assets are currently restricted",
          blockedLabel: "Blocked"
        },
      
      },
      "rightSidebar": {
          "title": "Signals",
          "liveScanner": "Live Scanner",
          "demoBalance": "Demo Balance Used",
          "top": "Top",
          "price": "Price",
          "aiScore": "AI Score",
          "rsiStrength": "RSI Strength",
          "lev": "LEV",
          "risk": "Risk",
          "target": "Target",
          "scanning": "Scanning...",
          "scanningDesc": "Searching for BUY/SELL patterns"
        },
        "signals": {
          "scalpBuy": "SCALP BUY",
          "smartBuy": "SMART BUY",
          "trendBuy": "TREND BUY",
          "scalpSell": "SCALP SELL",
          "smartSell": "SMART SELL",
          "trendSell": "TREND SELL"
        },
      "nav": {
          "terminal": "Terminal",
          "backtest": "Backtest",
          "cabinet": "Cabinet",
          "api": "API Keys",
          "website": "Website"
        },
        "backtest": {
          "title": "Strategy Tester",
          "subtitle": "Check V8.0 logic on historical data",
          "pairLabel": "Trading Pair",
          "periodLabel": "Period (Days)",
          "runBtn": "Run Test",
          "processing": "Processing...",
          "error": "Error running test. Check your connection.",
          
          "info1Title": "V8.0 Algorithm",
          "info1Desc": "Uses real historical data to test RSI, Bollinger, and Volume strategies.",
          "info2Title": "Realistic Execution",
          "info2Desc": "Simulates slippage (0.02%) and exchange fees (0.05%) for fair results.",
          "info3Title": "Optimization",
          "info3Desc": "Find the best parameters for scalping or panic trading before going live.",

          "totalProfit": "Total Profit",
          "winRate": "Win Rate",
          "totalTrades": "Total Trades",
          "drawdown": "Max Drawdown",
          "equityCurve": "Equity Curve",
          "history": "Trade History",
          
          "colSignal": "Signal",
          "colType": "Type",
          "colEntry": "Entry",
          "colExit": "Exit",
          "colResult": "Result"
        },
          "common": {
            "loading": "Loading..."
          },
          "profile": {
            "title": "Personal Cabinet",
            "subtitle": "Account management and risk settings",
            "logout": "Logout",
            "connected": "Connected",
            "notLinked": "Not Linked",
            "upgradeTitle": "Upgrade to PRO",
            "upgradeDesc": "Remove limits and get access to Smart Scanner V8.0.",
            "upgradeBtn": "Choose Plan",
            "riskTitle": "Risk Management",
            "riskDesc": "Security parameters for your bot",
            "riskPerTrade": "Risk Per Trade (%)",
            "riskPerTradeDesc": "% of deposit lost if Stop Loss is hit.",
            "maxDailyLoss": "Max Daily Loss (%)",
            "maxDailyLossDesc": "Bot stops if daily loss exceeds this value.",
            "maxTrades": "Max Concurrent Trades",
            "generalTitle": "General Settings",
            "generalDesc": "Interface language and notifications",
            "telegramNotif": "Telegram Notifications",
            "saveBtn": "Save Settings",
            "saving": "Saving...",
            "saveSuccess": "Settings saved successfully!",
            "saveError": "Error saving settings"
          }, 
      ai: {
            calibration: {
              title: "AI Brain Calibration 🧠",
              message: "I need more data (min 5 trades) to find hidden patterns in your trading."
            },
            tilt: {
              title: "🛑 TILT DETECTED",
              message: "3 consecutive losses detected. Statistics show 90% chance your next trade will fail.",
              action: "Close terminal for 2h"
            },
            hold: {
              title: "\"Hope\" Syndrome",
              message: "You take profits fast ({{winTime}} min) but hold losses too long ({{lossTime}} min).",
              action: "Use hard Stop-Loss"
            },
            rr: {
              title: "Math Trap",
              message: "Avg Loss (${{loss}}) is bigger than Avg Win (${{win}}). Deposit at risk.",
              action: "Reduce risk / Increase TP"
            },
            toxic: {
              title: "Deposit Vampire: {{coin}}",
              message: "You lost ${{loss}} on {{coin}}. Strategy fails here.",
              action: "Blacklist coin"
            },
            sniper: {
              title: "Sniper Mode 🎯",
              message: "Amazing accuracy! WinRate {{winRate}}%. You are in the flow."
            },
            gold: {
              title: "Alpha Asset: {{coin}} 💎",
              message: "You trade {{coin}} perfectly. Profit: +${{profit}}."
            },
            neutral: {
              title: "Stable Flight",
              message: "WinRate: {{winRate}}%. PnL: ${{pnl}}. No anomalies detected."
            }
          },
      auth: {
          welcome: "Welcome Back",
          create: "Create Account",
          loginDesc: "Login to trading terminal",
          registerDesc: "Get $10,000 demo funds",
          email: "Email",
          password: "Password",
          placeholderEmail: "name@example.com",
          placeholderPass: "••••••••",
          loginBtn: "Login",
          registerBtn: "Register",
          processing: "Processing...",
          noAccount: "No account?",
          hasAccount: "Have an account?",
          switchLogin: "Login",
          switchRegister: "Sign Up",
          error: "Server Error"
      },
      guide: guideEN
    }
  },

  // --- RUSSIAN ---
  ru: {
    translation: {
      landing: {
        nav: { howItWorks: "Как это работает", features: "Возможности", reports: "Аналитика", pricing: "Тарифы", faq: "FAQ", login: "Вход", start: "Начать бесплатно", terminal: "Терминал", settings: "Настройки", billing: "Биллинг" },
        hero: { badge: "V6.4: TrendGuard™ AI Активирован", title: "Торгуй криптой как", subtitle: "Алгоритмический Фонд", desc: "Подключите Binance или Bybit за 2 минуты. Защита от сливов, анализ трендов и авто-риск менеджмент.", cta: "Запустить Бота", demo: "Смотреть Демо" },
        stats: { volume: "Объем торгов", traders: "Трейдеров", uptime: "Аптайм", speed: "Скорость" },
        how: { title: "Как начать зарабатывать?", subtitle: "Вам не нужно быть программистом или трейдером. Мы сделали всё за вас.", step1: { title: "Создайте аккаунт", desc: "Регистрация занимает 30 секунд. Вы сразу получите $10,000 на демо-счет для тестов." }, step2: { title: "Подключите биржу", desc: "Введите API ключи от Binance или Bybit. Мы не имеем доступа к выводу средств." }, step3: { title: "Выберите стратегию", desc: "Включите режим 'Trend' для консервативной торговли или 'Scalp' для агрессивной." } },
        fake: { mode: "РЕЖИМ ТОРГОВЛИ", risk: "РИСК НА СДЕЛКУ" },
        features: { title: "Технологии Wall Street у вас дома", subtitle: "Мы объединили 3 мощных алгоритма в одной платформе.", c1: { title: "Мульти-Биржа", desc: "Торгуйте одновременно на Binance Futures и Bybit. Единый интерфейс." }, c2: { title: "Защита TrendGuard™", desc: "Умный фильтр на базе EMA 200. Бот автоматически запрещает покупки на падающем рынке." }, c3: { title: "Smart Entry V2", desc: "Снайперский вход. Бот находит идеальные точки на основе RSI и паттернов." } },
        pricing: { 
          title: "Прозрачные Тарифы", 
          subtitle: "Начните с малого или сэкономьте до 20% на годовой подписке.", 
          period: "/мес", 
          select: "Выбрать", 
          p1: { title: "Старт", desc: "Для новичков", f1: "1 Биржа", f2: "Базовая защита", f3: "Telegram сигналы", f4: "Лимит: $5,000" }, 
          p2: { title: "Трейдер", desc: "Популярный", f1: "Binance + Bybit", f2: "TrendGuard™ Pro", f3: "Лимит: $20,000", f4: "Scalp Mode" }, 
          p3: { title: "Профи", desc: "Максимальный", f1: "Все биржи", f2: "Безлимитный деп", f3: "VIP Поддержка", f4: "Ранний доступ" }, 
          p4: { title: "Инвестор", desc: "Автопилот", f1: "White Label", f2: "API Доступ", f3: "Личный менеджер", f4: "Скидка навсегда" } 
        },
        contact: { title: "Остались вопросы?", desc: "Не уверены, какой тариф выбрать? Наша поддержка отвечает за 15 минут.", telegram: "Написать в Telegram", email: "Написать на Email" },
        faq: { title: "Часто задаваемые вопросы", subtitle: "Всё, что вы хотели знать о безопасности и работе бота.", q1: { q: "Безопасно ли давать API ключи?", a: "Абсолютно. Вы включаете 'Futures Trading', но НЕ включаете 'Withdrawal' (Вывод)." }, q2: { q: "Сколько можно заработать?", a: "Зависит от рынка. В среднем на тарифе 'Trend' 5-15% в месяц." }, q3: { q: "Что если интернет пропадет?", a: "Бот работает на облаке 24/7." }, q4: { q: "Можно ли попробовать бесплатно?", a: "Да! Вы получаете тариф FREE и $10,000 виртуальных средств." }, q5: { q: "Какие пары торгует бот?", a: "Бот сканирует топ-50 монет (BTC, ETH, SOL и др.)." } },
        footer: { terms: "Условия использования", privacy: "Конфиденциальность", support: "Поддержка" }
        },
        platform: {
          nav: { dashboard: "Обзор", history: "История", reports: "Аналитика", profile: "Профиль", billing: "Подписка", system: "СИСТЕМА", settings: "API Ключи", guide: "Справка", admin: "Админка", logout: "Выйти" },
          risk: { title: "Параметры Риска", risk: "Риск", leverage: "Плечо", tp: "Тейк", sl: "Стоп" },
          btc: { title: "BTC Рынок", safe: "SAFE", risk: "RISK" },
          actions: { sim: "Симуляция", real: "Реал", start: "СТАРТ", stop: "СТОП", panic: "PANIC", support: "Чат", active: "АКТИВЕН", paused: "ПАУЗА" },
          dashboard: { title: "Терминал", changeMode: "Режим", live: "Live", refresh: "Обновить", updating: "Обновление...", paperTitleFree: "Демо (Free)", paperTitlePro: "Демо (Pro)", paperDescFree: "Виртуальные средства.", paperDescPro: "Для тестирования используется торговля на Демо. Переключитесь на реальный режим на боковой панели.", connectReal: "Подключить Реал", virtualBal: "ВИРТ. БАЛАНС", realBal: "РЕАЛ. БАЛАНС", pnl: "Прибыль (24ч)", deals: "Сделки", winrate: "Винрейт", chart: "График", pnlDyn: "Динамика", heatmap: "Карта" },
          manualTrade: { title: "Ручная Торговля", symbol: "Пара", margin: "Маржа", leverage: "Плечо", positionSize: "Объем:", long: "LONG", short: "SHORT", confirm: "Открыть {{type}} на {{symbol}}?", success: "Ордер создан!", error: "Ошибка: " },
          logs: { title: "Логи Системы", waiting: "Ожидание событий..." },
          ai: { title: "AI Анализ" },
          telegram: { title: "Telegram", subtitle: "Привязка", step1: "1. Откройте", step2: "2. Введите:", codeLabel: "Код", copy: "Копировать", waiting: "Ожидание...", active: "Активен" },
          apiSettings: { title: "API Настройки", success: "Сохранено", platform: "Платформа", platformDesc: "Выбор биржи", keysTitle: "Ключи", keysDesc: "Введите ключи", apiKey: "API Key", apiSecret: "Secret Key", placeholderKey: "Вставьте Key", placeholderSecret: "Вставьте Secret", placeholderSecretHidden: "Скрыт", keySaved: "Сохранен", saveBtn: "СОХРАНИТЬ", saving: "Сохранение...", current: "Биржа:", currentDesc: "Торговля на:", currentKey: "Конец:" },
          footer: { docs: "Доки", made: "Сделано" },
          errors: { chartReload: "График перезагружается...", refresh: "Обновить" },
          history: { colAsset: "Актив / Стратегия", colDetails: "Детали Входа", colVolume: "Объем (USDT)", colPnl: "PnL & ROI", colAction: "Управление", qty: "Кол-во", trading: "Торгуется", filled: "Исполнен", close: "Закрыть По рынку", closing: "Closing...", emptyTitle: "Журнал операций пуст", legendSim: "Симуляция", legendReal: "Реальный Счет", totalOps: "Всего операций:", confirmClose: "Закрыть позицию {{symbol}} по рынку?", closeSuccess: "✅ Позиция {{symbol}} закрыта вручную", error: "Ошибка: " },
          strategy: {
            advancedTitle: "Продвинутая стратегия",
            dcaTitle: "DCA (Мартингейл)",
            dcaDesc: "Усреднение позиции при просадке. Бот докупает актив, чтобы снизить среднюю цену входа.",
            dcaStep: "Шаг (%)",
            dcaMult: "Множитель",
            dcaMax: "Макс. сделок",
            trailingTitle: "Трейлинг Тейк-Профит",
            trailingDesc: "Позволяет прибыли расти, следуя за ценой. Стоп-лосс перемещается вверх вместе с ростом курса.",
            trailingAct: "Активация (%)",
            trailingCall: "Отступ (%)"
          },
          riskInfo: {
            "balanceUsageDesc": "Бот будет использовать не более указанного процента от общего депозита.",
            "riskDesc": "Процент риска от баланса на одну сделку.",
            "leverageDesc": "Кредитное плечо для торговли фьючерсами.",
            "concurrentDesc": "Максимальное количество одновременно открытых позиций.",
            "dailyLossDesc": "Лимит убытка за день. При достижении бот прекратит торговлю до следующего дня.",
            "minVolumeDesc": "Фильтр монет по минимальному суточному объему торгов в USDT."
          },
          reports: {
            title: "Аналитика торговли",
            desc: "Глубокий анализ эффективности вашей стратегии",
            refresh: "Обновить данные",
            loading: "Анализ сделок...",
            profit: "Чистая прибыль",
            winrate: "Винрейт",
            totalTrades: "Всего сделок",
            activeBans: "В бане",
            chartTitle: "Кривая роста (Дневной PnL)",
            performanceTitle: "Эффективность активов",
            tradesCount: "{{count}} сделок",
            blacklistTitle: "Ограниченные активы (Blacklist)",
            bannedUntil: "Заблокирован до",
            noBans: "На данный момент ограничений нет",
            blockedLabel: "Заблокирован"
          },
          
        },
        
        "rightSidebar": {
          "title": "Сигналы",
          "liveScanner": "Live Сканер",
          "demoBalance": "Используется Демо Баланс",
          "top": "Топ",
          "price": "Цена",
          "aiScore": "AI Score",
          "rsiStrength": "Сила RSI",
          "lev": "Плечо",
          "risk": "Риск",
          "target": "Цель",
          "scanning": "Сканирование...",
          "scanningDesc": "Ищем паттерны BUY/SELL"
        },
        "signals": {
          "scalpBuy": "SCALP BUY",
          "smartBuy": "SMART BUY",
          "trendBuy": "TREND BUY",
          "scalpSell": "SCALP SELL",
          "smartSell": "SMART SELL",
          "trendSell": "TREND SELL"
        },
                "nav": {
          "terminal": "Терминал",
          "backtest": "Бэктест",
          "cabinet": "Кабинет",
          "api": "API Ключи",
          "website": "Главная"
        },
            "backtest": {
            "title": "Тестер Стратегий",
            "subtitle": "Проверка логики V8.0 на исторических данных",
            "pairLabel": "Торговая пара",
            "periodLabel": "Период (Дни)",
            "runBtn": "Запустить Тест",
            "processing": "Обработка...",
            "error": "Ошибка запуска теста. Проверьте соединение.",
            
            "info1Title": "Алгоритм V8.0",
            "info1Desc": "Использует реальные исторические данные для проверки стратегий RSI, Bollinger и Volume.",
            "info2Title": "Реальное Исполнение",
            "info2Desc": "Симулирует проскальзывание (0.02%) и комиссии биржи (0.05%) для честных результатов.",
            "info3Title": "Оптимизация",
            "info3Desc": "Найдите лучшие параметры для скальпинга или паник-трейдинга перед игрой на реальные деньги.",

            "totalProfit": "Общая Прибыль",
            "winRate": "Винрейт",
            "totalTrades": "Всего Сделок",
            "drawdown": "Макс. Просадка",
            "equityCurve": "Кривая Капитала",
            "history": "История Сделок",
            
            "colSignal": "Сигнал",
            "colType": "Тип",
            "colEntry": "Вход",
            "colExit": "Выход",
            "colResult": "Результат"
          },
        "common": {
          "loading": "Загрузка..."
        },
        "profile": {
          "title": "Личный Кабинет",
          "subtitle": "Управление аккаунтом и настройки риска",
          "logout": "Выход",
          "connected": "Подключен",
          "notLinked": "Не подключен",
          "upgradeTitle": "Перейти на PRO",
          "upgradeDesc": "Снимите лимиты и получите доступ к Smart Scanner V8.0.",
          "upgradeBtn": "Выбрать Тариф",
          "riskTitle": "Управление Риском",
          "riskDesc": "Параметры безопасности вашего бота",
          "riskPerTrade": "Риск на сделку (%)",
          "riskPerTradeDesc": "Какой процент депозита теряется при Stop Loss.",
          "maxDailyLoss": "Макс. убыток за день (%)",
          "maxDailyLossDesc": "Бот остановится, если убыток превысит это значение.",
          "maxTrades": "Одновременных сделок",
          "generalTitle": "Общие настройки",
          "generalDesc": "Язык интерфейса и уведомления",
          "telegramNotif": "Уведомления Telegram",
          "saveBtn": "Сохранить настройки",
          "saving": "Сохранение...",
          "saveSuccess": "Настройки успешно сохранены!",
          "saveError": "Ошибка сохранения настроек"
        },
        ai: {
          "advisor_label": "ИИ АНАЛИТИК",
          "actions": {
            "block_success": "🚫 Монета {{coin}} добавлена в черный список на 24 часа.",
            "pause_success": "⏸️ Бот поставлен на паузу. Отдохните!",
            "block_btn": "Блок 24ч",
            "pause_btn": "Стоп Бот"
          },
          "toxic": {
            "title": "Токсичный актив",
            "message": "Монета {{coin}} тянет вас вниз. Убыток составил -{{loss}}$. Рекомендую исключить её.",
            "action": "Блокировать {{coin}}"
          },
          "tilt": {
            "title": "Угроза Тильта",
            "message": "Вы поймали серию из 3 убытков подряд. Эмоции могут привести к сливу депозита.",
            "action": "Остановить торговлю"
          },
          "sniper": {
            "title": "Режим Снайпера",
            "message": "Отличная работа! Ваш WinRate {{winRate}}% выше рынка. Продолжайте в том же духе."
          },
          "gold": {
              "title": "Золотая Жила",
              "message": "Монета {{coin}} принесла вам +{{profit}}$. Это ваш лучший актив на этой неделе."
            },
            "rr": {
              "title": "Математическая Ловушка",
              "message": "Ваши убытки ({{loss}}$) в среднем больше, чем прибыли ({{win}}$). Нужно менять стратегию."
            },
            "hold": {
              "title": "Синдром Надежды",
              "message": "Вы держите убыточные сделки в {{winTime}} раз дольше прибыльных. Режьте лосей быстрее!",
              "action": "Закрыть убытки"
            },
            "neutral": {
              "title": "Стабильный Полет",
              "message": "Рынок спокоен. Ваш WinRate {{winRate}}%. Текущий PnL: {{pnl}}$."
            },
            "calibration": {
              "title": "Калибровка ИИ",
              "message": "Мне нужно еще несколько сделок, чтобы проанализировать ваш стиль торговли."
            }
            
          },
                    
      auth: { welcome: "Привет", create: "Регистрация", loginDesc: "Вход", registerDesc: "Демо счет", email: "Email", password: "Пароль", placeholderEmail: "email@ex.com", placeholderPass: "***", loginBtn: "Войти", registerBtn: "Создать", processing: "...", noAccount: "Нет аккаунта?", hasAccount: "Есть аккаунт?", switchLogin: "Вход", switchRegister: "Регистрация", error: "Ошибка" },
      guide: guideRU
    }
  },

  // --- GERMAN (DE) ---
  de: {
    translation: {
      landing: {
        nav: { howItWorks: "Wie es funktioniert", features: "Funktionen", "reports": "Analytik", pricing: "Preise", faq: "FAQ", login: "Anmelden", start: "Starten", terminal: "Terminal", settings: "Einstellungen" },
        hero: { badge: "V6.4: TrendGuard™ AI Aktiviert", title: "Handeln Sie Krypto wie ein", subtitle: "Algorithmus-Fonds", desc: "Verbinden Sie Binance oder Bybit in 2 Minuten. Schutz vor Verlusten, Trendanalyse und Auto-Risikomanagement.", cta: "Bot Starten", demo: "Live-Demo" },
        stats: { volume: "Handelsvolumen", traders: "Aktive Trader", uptime: "Betriebszeit", speed: "Geschwindigkeit" },
        how: { title: "Wie fange ich an?", subtitle: "Sie müssen kein Programmierer sein.", step1: { title: "Konto erstellen", desc: "Registrierung dauert 30 Sekunden." }, step2: { title: "Börse verbinden", desc: "Geben Sie API-Schlüssel ein." }, step3: { title: "Strategie wählen", desc: "Wählen Sie 'Trend' oder 'Scalp'." } },
        fake: { mode: "HANDELSMODUS", risk: "RISIKO PRO TRADE" },
        features: { title: "Wall Street Technologie zu Hause", subtitle: "3 leistungsstarke Algorithmen.", c1: { title: "Multi-Exchange", desc: "Binance und Bybit gleichzeitig." }, c2: { title: "TrendGuard™ Schutz", desc: "Verhindert Käufe in fallenden Märkten." }, c3: { title: "Smart Entry V2", desc: "Präziser Einstieg mit RSI." } },
        pricing: { title: "Preise", subtitle: "Sparen Sie 20% jährlich.", period: "/mon", select: "Wählen", p1: { title: "Start", desc: "Für Anfänger", f1: "1 Börse", f2: "Basisschutz", f3: "Signale", f4: "Limit: 5.000 $" }, p2: { title: "Trader", desc: "Beliebt", f1: "Binance + Bybit", f2: "TrendGuard™ Pro", f3: "Limit: 20.000 $", f4: "Scalp Modus" }, p3: { title: "Pro", desc: "Maximal", f1: "Alle Börsen", f2: "Unbegrenzte", f3: "VIP Support", f4: "Early Access" }, p4: { title: "Investor", desc: "Autopilot", f1: "White Label", f2: "API Zugang", f3: "Persönlicher Manager", f4: "Rabatt" } },
        contact: { title: "Noch Fragen?", desc: "Unser Support antwortet schnell.", telegram: "Telegram", email: "Email" },
        faq: { title: "FAQ", subtitle: "Sicherheit und Funktion.", q1: { q: "Ist es sicher?", a: "Ja. Keine Auszahlungen." }, q2: { q: "Gewinn?", a: "5-15% pro Monat." }, q3: { q: "Internet?", a: "Cloud 24/7." }, q4: { q: "Kostenlos?", a: "Ja, Demo-Modus." }, q5: { q: "Coins?", a: "Top-50 Coins." } },
        footer: { terms: "Nutzungsbedingungen", privacy: "Datenschutz", support: "Unterstützung" }
      },
      platform: {
        nav: { dashboard: "Übersicht", history: "Verlauf", profile: "Profil", billing: "Abonnement", system: "SYSTEM", settings: "API Schlüssel", guide: "Anleitung", admin: "Admin", logout: "Abmelden" },
        risk: { title: "Risikoparameter", risk: "Risiko", leverage: "Hebel", tp: "Take Profit", sl: "Stop Loss" },
        btc: { title: "BTC Markt", safe: "SICHER", risk: "RISIKO" },
        actions: { sim: "Simulation (Paper)", real: "Echtgeld (Live)", start: "SYSTEM STARTEN", stop: "STOPPEN", panic: "NOT-AUS", support: "Support-Chat", active: "SYSTEM AKTIV", paused: "PAUSIERT" },
        dashboard: { title: "Analyse-Terminal", changeMode: "Modus ändern", live: "Live-Daten", refresh: "Aktualisieren", updating: "Laden...", paperTitleFree: "Simulationsmodus (Free)", paperTitlePro: "Simulationsmodus (Pro)", paperDescFree: "Virtuelles Guthaben.", paperDescPro: "Testmodus aktiv.", connectReal: "Echtgeld-Konto verbinden", virtualBal: "VIRTUELL", realBal: "ECHTGELD", pnl: "Gesamt PnL (24h)", deals: "Aktive Deals", winrate: "Gewinnrate", chart: "Chartanalyse", pnlDyn: "PnL Dynamik", heatmap: "Markt-Heatmap" },
        manualTrade: { title: "Manueller Handel", symbol: "Handelspaar", margin: "Marge (USDT)", leverage: "Hebel (x)", positionSize: "Positionsgröße:", long: "LONG", short: "SHORT", confirm: "{{type}} auf {{symbol}} mit Marge ${{amount}} öffnen?", success: "Order erfolgreich platziert!", error: "Fehler: " },
        logs: { title: "System-Live-Logs", waiting: "Warte auf Server-Ereignisse..." },
        ai: { title: "KI-Analyse" },
        telegram: { title: "Telegram-Verwaltung", subtitle: "Sichere Kontoverknüpfung", step1: "1. Öffnen Sie", step2: "2. Befehl eingeben:", codeLabel: "Bestätigungscode", copy: "Befehl kopieren", waiting: "Warte auf Telegram...", active: "Telegram aktiv" },
        apiSettings: { title: "API-Einstellungen", success: "Einstellungen gespeichert für", platform: "Plattformwahl", platformDesc: "Wählen Sie die Börse", keysTitle: "Zugangsschlüssel", keysDesc: "Schlüssel eingeben für", apiKey: "API Key", apiSecret: "Secret Key", placeholderKey: "API Key einfügen von", placeholderSecret: "Secret Key einfügen von", placeholderSecretHidden: "Schlüssel versteckt (neu eingeben)", keySaved: "Schlüssel gespeichert.", saveBtn: "SPEICHERN FÜR", saving: "Speichern...", current: "Aktive Börse:", currentDesc: "Bot handelt auf:", currentKey: "Endet mit:" },
        footer: { docs: "Dokumentation", made: "Gemacht mit" },
        errors: { chartReload: "Widget wird neu geladen...", refresh: "Aktualisieren" },
        history: {
          colAsset: "Asset / Strategie",
          colDetails: "Einstiegsdetails",
          colVolume: "Volumen (USDT)",
          colPnl: "PnL & ROI",
          colAction: "Verwaltung",
          qty: "Menge",
          trading: "Wird gehandelt",
          filled: "Ausgeführt",
          close: "Zum Marktpreis schließen",
          closing: "Schließen...",
          emptyTitle: "Handelsverlauf ist leer",
          legendSim: "Simulation",
          legendReal: "Reales Konto",
          totalOps: "Gesamtoperationen:",
          confirmClose: "Position {{symbol}} zum Marktpreis schließen?",
          closeSuccess: "✅ Position {{symbol}} manuell geschlossen",
          error: "Fehler: "
        },
        "strategy": {
          "advancedTitle": "Erweiterte Strategie",
          "dcaTitle": "DCA (Martingale)",
          "dcaDesc": "Positionsdurchschnitt bei Rückgang. Der Bot kauft nach, um den durchschnittlichen Einstiegspreis zu senken.",
          "dcaStep": "Schritt (%)",
          "dcaMult": "Multiplikator",
          "dcaMax": "Max. Aufträge",
          "trailingTitle": "Trailing Take Profit",
          "trailingDesc": "Lässt Gewinne mit dem Preis wachsen. Der Stop-Loss steigt, wenn der Preis steigt.",
          "trailingAct": "Aktivierung (%)",
          "trailingCall": "Rücklauf (%)"
        },
        "riskInfo": {
          "balanceUsageDesc": "Der Bot verwendet nicht mehr als den angegebenen Prozentsatz des gesamten Guthabens.",
          "riskDesc": "Prozentsatz des Kontostands, der bei einem einzelnen Trade riskiert wird.",
          "leverageDesc": "Hebel für den Futures-Handel.",
          "concurrentDesc": "Maximale Anzahl gleichzeitig geöffneter Positionen.",
          "dailyLossDesc": "Tägliches Verlustlimit. Nach Erreichen stoppt der Bot den Handel bis zum nächsten Tag.",
          "minVolumeDesc": "Filter für Coins nach minimalem täglichem Handelsvolumen in USDT."
        },
        "reports": {
          "title": "Handelsanalyse",
          "desc": "Tiefgehende Analyse der Leistung Ihrer Handelsstrategie",
          "refresh": "Daten aktualisieren",
          "loading": "Trades werden analysiert...",
          "profit": "Gesamtnettogewinn",
          "winrate": "Gewinnquote",
          "totalTrades": "Gesamtanzahl Trades",
          "activeBans": "Aktive Sperren",
          "chartTitle": "Wachstumskurve (Täglicher PnL)",
          "performanceTitle": "Asset-Performance",
          "tradesCount": "{{count}} Trades",
          "blacklistTitle": "Eingeschränkte Assets (Blacklist)",
          "bannedUntil": "Gesperrt bis",
          "noBans": "Derzeit sind keine Assets gesperrt",
          "blockedLabel": "Gesperrt"
        }
      },
      rightSidebar: {
        title: "Signale",
        liveScanner: "Live-Scanner",
        demoBalance: "Demo-Guthaben verwendet",
        top: "Top",
        price: "Preis",
        aiScore: "KI-Score",
        rsiStrength: "RSI-Stärke",
        lev: "Hebel",
        risk: "Risiko",
        target: "Ziel",
        scanning: "Scannen...",
        scanningDesc: "Suche nach KAUF/VERKAUF-Mustern"
      },
      signals: {
        scalpBuy: "SCALP KAUF",
        smartBuy: "SMART KAUF",
        trendBuy: "TREND KAUF",
        scalpSell: "SCALP VERKAUF",
        smartSell: "SMART VERKAUF",
        trendSell: "TREND VERKAUF"
      },
      nav: {
        terminal: "Terminal",
        backtest: "Backtest",
        cabinet: "Konto",
        api: "API-Schlüssel",
        website: "Startseite",
      },
      "backtest": {
        "title": "Strategie-Tester",
        "subtitle": "Überprüfen Sie die V8.0-Logik anhand historischer Daten",
        "pairLabel": "Handelspaar",
        "periodLabel": "Zeitraum (Tage)",
        "runBtn": "Test starten",
        "processing": "Verarbeitung...",
        "error": "Fehler beim Testlauf. Überprüfen Sie die Verbindung.",
        
        "info1Title": "V8.0 Algorithmus",
        "info1Desc": "Verwendet echte historische Daten, um RSI-, Bollinger- und Volumenstrategien zu testen.",
        "info2Title": "Realistische Ausführung",
        "info2Desc": "Simuliert Slippage (0,02%) und Börsengebühren (0,05%) für faire Ergebnisse.",
        "info3Title": "Optimierung",
        "info3Desc": "Finden Sie die besten Parameter für Scalping oder Panic-Trading vor dem Live-Handel.",

        "totalProfit": "Gesamtgewinn",
        "winRate": "Gewinnrate",
        "totalTrades": "Gesamtanzahl Trades",
        "drawdown": "Max. Drawdown",
        "equityCurve": "Kapitalverlauf",
        "history": "Handelsverlauf",
        
        "colSignal": "Signal",
        "colType": "Typ",
        "colEntry": "Einstieg",
        "colExit": "Ausstieg",
        "colResult": "Ergebnis"
      },
      common: {
        loading: "Lädt..."
      },
      profile: {
        title: "Persönliches Konto",
        subtitle: "Kontoverwaltung und Risikoeinstellungen",
        logout: "Abmelden",
        connected: "Verbunden",
        notLinked: "Nicht verbunden",
        upgradeTitle: "Upgrade auf PRO",
        upgradeDesc: "Entfernen Sie Limits und erhalten Sie Zugriff auf Smart Scanner V8.0.",
        upgradeBtn: "Plan wählen",
        riskTitle: "Risikomanagement",
        riskDesc: "Sicherheitsparameter für Ihren Bot",
        riskPerTrade: "Risiko pro Trade (%)",
        riskPerTradeDesc: "% des Kapitals, das verloren geht, wenn der Stop-Loss ausgelöst wird.",
        maxDailyLoss: "Maximaler Tagesverlust (%)",
        maxDailyLossDesc: "Der Bot stoppt, wenn der Tagesverlust diesen Wert überschreitet.",
        maxTrades: "Max. gleichzeitige Trades",
        generalTitle: "Allgemeine Einstellungen",
        generalDesc: "Sprache der Oberfläche und Benachrichtigungen",
        telegramNotif: "Telegram-Benachrichtigungen",
        saveBtn: "Einstellungen speichern",
        saving: "Speichern...",
        saveSuccess: "Einstellungen erfolgreich gespeichert!",
        saveError: "Fehler beim Speichern der Einstellungen"
      },
      ai: {
        calibration: {
          title: "AI-Gehirn-Kalibrierung 🧠",
          message: "Ich benötige mehr Daten (mindestens 5 Trades), um verborgene Muster in deinem Trading zu erkennen."
        },
        tilt: {
          title: "🛑 TILT ERKANNT",
          message: "3 aufeinanderfolgende Verlusttrades erkannt. Statistisch liegt die Wahrscheinlichkeit bei 90%, dass der nächste Trade ebenfalls scheitert.",
          action: "Terminal für 2 Std. schließen"
        },
        hold: {
          title: "„Hoffnungs“-Syndrom",
          message: "Du nimmst Gewinne schnell mit ({{winTime}} Min), hältst Verluste aber zu lange ({{lossTime}} Min).",
          action: "Harten Stop-Loss setzen"
        },
        rr: {
          title: "Mathematische Falle",
          message: "Der durchschnittliche Verlust (${{loss}}) ist höher als der durchschnittliche Gewinn (${{win}}). Kapital in Gefahr.",
          action: "Risiko senken / TP erhöhen"
        },
        toxic: {
          title: "Depot-Vampir: {{coin}}",
          message: "Du hast ${{loss}} mit {{coin}} verloren. Die Strategie funktioniert hier nicht.",
          action: "Coin auf Blacklist setzen"
        },
        sniper: {
          title: "Sniper-Modus 🎯",
          message: "Beeindruckende Genauigkeit! WinRate {{winRate}}%. Du bist im Flow."
        },
        gold: {
          title: "Alpha-Asset: {{coin}} 💎",
          message: "Du handelst {{coin}} perfekt. Gewinn: +${{profit}}."
        },
        neutral: {
          title: "Stabiler Flug",
          message: "WinRate: {{winRate}}%. PnL: ${{pnl}}. Keine Auffälligkeiten erkannt."
        }
      },
      auth: { welcome: "Willkommen zurück", create: "Konto erstellen", loginDesc: "Einloggen ins Terminal", registerDesc: "10.000$ Demo-Guthaben erhalten", email: "E-Mail", password: "Passwort", placeholderEmail: "name@example.com", placeholderPass: "••••••••", loginBtn: "Einloggen", registerBtn: "Registrieren", processing: "Verarbeitung...", noAccount: "Kein Konto?", hasAccount: "Haben Sie ein Konto?", switchLogin: "Anmelden", switchRegister: "Registrieren", error: "Serverfehler" },
      guide: guideDE
    }
  },

  // --- SPANISH (ES) ---
  es: {
    translation: {
      landing: {
        nav: { howItWorks: "Cómo funciona", features: "Características", "reports": "Analítica", pricing: "Precios", faq: "FAQ", login: "Acceso", start: "Empezar", terminal: "Terminal", settings: "Ajustes" },
        hero: { badge: "V6.4: TrendGuard™ AI Activado", title: "Opera Cripto como un", subtitle: "Fondo Algorítmico", desc: "Conecta Binance o Bybit en 2 minutos.", cta: "Iniciar Bot", demo: "Demo en Vivo" },
        stats: { volume: "Volumen", traders: "Traders", uptime: "Tiempo activo", speed: "Velocidad" },
        how: { title: "¿Cómo empezar?", subtitle: "No necesitas ser programador.", step1: { title: "Crear cuenta", desc: "Registro en 30 segundos." }, step2: { title: "Conectar Exchange", desc: "Ingresa claves API." }, step3: { title: "Elegir estrategia", desc: "Usa 'Trend' o 'Scalp'." } },
        fake: { mode: "MODO TRADING", risk: "RIESGO" },
        features: { title: "Tecnología Wall Street", subtitle: "3 algoritmos potentes.", c1: { title: "Multi-Exchange", desc: "Binance y Bybit simultáneamente." }, c2: { title: "Protección TrendGuard™", desc: "Evita compras en bajadas." }, c3: { title: "Smart Entry V2", desc: "Entrada precisa RSI." } },
        pricing: { title: "Precios", subtitle: "Ahorra 20% anual.", period: "/mes", select: "Seleccionar", p1: { title: "Inicio", desc: "Principiantes", f1: "1 Exchange", f2: "Protección Básica", f3: "Señales", f4: "Límite: $5,000" }, p2: { title: "Trader", desc: "Popular", f1: "Binance + Bybit", f2: "TrendGuard™ Pro", f3: "Límite: $20,000", f4: "Modo Scalp" }, p3: { title: "Pro", desc: "Máximo", f1: "Todos", f2: "Depósito Ilimitado", f3: "Soporte VIP", f4: "Acceso Anticipado" }, p4: { title: "Inversor", desc: "Automático", f1: "Marca Blanca", f2: "API", f3: "Gerente", f4: "Descuento" } },
        contact: { title: "¿Preguntas?", desc: "Soporte rápido.", telegram: "Telegram", email: "Email" },
        faq: { title: "Preguntas Frecuentes", subtitle: "Seguridad.", q1: { q: "¿Es seguro?", a: "Sí. Sin retiros." }, q2: { q: "¿Ganancias?", a: "5-15% mensual." }, q3: { q: "¿Internet?", a: "Nube 24/7." }, q4: { q: "¿Gratis?", a: "Sí, modo Demo." }, q5: { q: "¿Monedas?", a: "Top 50." } },
        footer: { terms: "Términos", privacy: "Privacidad", support: "Soporte" }
      },
      platform: {
        nav: { dashboard: "Resumen", history: "Historial", profile: "Perfil", billing: "Suscripción", system: "SISTEMA", settings: "Claves API", guide: "Guía", admin: "Admin", logout: "Salir" },
        risk: { title: "Parámetros", risk: "Riesgo", leverage: "Apalancamiento", tp: "Take Profit", sl: "Stop Loss" },
        btc: { title: "Mercado BTC", safe: "SEGURO", risk: "RIESGO" },
        actions: { sim: "Simulación (Paper)", real: "Real (Live)", start: "INICIAR SISTEMA", stop: "DETENER", panic: "CIERRE PÁNICO", support: "Chat Soporte", active: "SISTEMA ACTIVO", paused: "PAUSADO" },
        dashboard: { title: "Terminal de Análisis", changeMode: "Cambiar Modo", live: "Datos en Vivo", refresh: "Actualizar", updating: "Cargando...", paperTitleFree: "Modo Simulación (Gratis)", paperTitlePro: "Modo Simulación (Pro)", paperDescFree: "Fondos virtuales.", paperDescPro: "Testando estrategias.", connectReal: "Conectar Real", virtualBal: "SALDO VIRTUAL", realBal: "SALDO REAL", pnl: "PnL Total (24h)", deals: "Operaciones", winrate: "Tasa Éxito", chart: "Gráfico", pnlDyn: "Dinámica PnL", heatmap: "Mapa de Calor" },
        manualTrade: { title: "Trading Manual", symbol: "Par de Trading", margin: "Margen (USDT)", leverage: "Apalancamiento (x)", positionSize: "Tamaño Posición:", long: "LARGO", short: "CORTO", confirm: "¿Abrir {{type}} en {{symbol}} con margen ${{amount}}?", success: "¡Orden enviada con éxito!", error: "Error: " },
        logs: { title: "Registros del Sistema", waiting: "Esperando eventos del servidor..." },
        ai: { title: "Análisis IA" },
        telegram: { title: "Gestión de Telegram", subtitle: "Vinculación segura", step1: "1. Abrir", step2: "2. Escribir comando:", codeLabel: "Código de verificación", copy: "Copiar comando", waiting: "Esperando solicitud...", active: "Telegram activo" },
        apiSettings: { title: "Ajustes de API", success: "Ajustes guardados para", platform: "Selección de Plataforma", platformDesc: "Elija exchange para configurar", keysTitle: "Claves de Acceso", keysDesc: "Ingrese claves para", apiKey: "API Key", apiSecret: "Secret Key", placeholderKey: "Pegar API Key de", placeholderSecret: "Pegar Secret Key de", placeholderSecretHidden: "Clave oculta (ingrese nueva)", keySaved: "Clave guardada.", saveBtn: "GUARDAR PARA", saving: "Guardando...", current: "Exchange Activo:", currentDesc: "El bot opera en:", currentKey: "Termina en:" },
        footer: { docs: "Documentación", made: "Hecho con" },
        errors: { chartReload: "Recargando gráfico...", refresh: "Actualizar" },
        history: {
          colAsset: "Activo / Estrategia",
          colDetails: "Detalles de entrada",
          colVolume: "Volumen (USDT)",
          colPnl: "PnL & ROI",
          colAction: "Gestión",
          qty: "Cantidad",
          trading: "Operando",
          filled: "Ejecutado",
          close: "Cerrar a mercado",
          closing: "Cerrando...",
          emptyTitle: "El historial de operaciones está vacío",
          legendSim: "Simulación",
          legendReal: "Cuenta real",
          totalOps: "Total de operaciones:",
          confirmClose: "¿Cerrar la posición {{symbol}} a mercado?",
          closeSuccess: "✅ Posición {{symbol}} cerrada manualmente",
          error: "Error: "
        },
        "strategy": {
          "advancedTitle": "Estrategia avanzada",
          "dcaTitle": "DCA (Martingala)",
          "dcaDesc": "Promedio de posición durante una caída. El bot compra más para reducir el precio medio de entrada.",
          "dcaStep": "Paso (%)",
          "dcaMult": "Multiplicador",
          "dcaMax": "Órdenes máximas",
          "trailingTitle": "Trailing Take Profit",
          "trailingDesc": "Permite que las ganancias sigan al precio. El Stop Loss sube cuando el precio aumenta.",
          "trailingAct": "Activación (%)",
          "trailingCall": "Retorno (%)"
        },
        "riskInfo": {
          "balanceUsageDesc": "El bot usará como máximo el porcentaje especificado del depósito total.",
          "riskDesc": "Porcentaje del balance arriesgado en una sola operación.",
          "leverageDesc": "Apalancamiento para trading de futuros.",
          "concurrentDesc": "Número máximo de posiciones abiertas simultáneamente.",
          "dailyLossDesc": "Límite de pérdida diaria. Al alcanzarlo, el bot dejará de operar hasta el día siguiente.",
          "minVolumeDesc": "Filtro de monedas por volumen mínimo diario en USDT."
        },
        "reports": {
            "title": "Analítica de Trading",
            "desc": "Análisis profundo del rendimiento de tu estrategia de trading",
            "refresh": "Actualizar datos",
            "loading": "Analizando operaciones...",
            "profit": "Beneficio neto total",
            "winrate": "Tasa de éxito",
            "totalTrades": "Total de operaciones",
            "activeBans": "Bloqueos activos",
            "chartTitle": "Curva de crecimiento (PnL diario)",
            "performanceTitle": "Rendimiento de activos",
            "tradesCount": "{{count}} operaciones",
            "blacklistTitle": "Activos restringidos (Lista negra)",
            "bannedUntil": "Bloqueado hasta",
            "noBans": "No hay activos restringidos actualmente",
            "blockedLabel": "Bloqueado"
          }
      },
      rightSidebar: {
        title: "Señales",
        liveScanner: "Escáner en vivo",
        demoBalance: "Saldo demo usado",
        top: "Top",
        price: "Precio",
        aiScore: "Puntuación IA",
        rsiStrength: "Fuerza RSI",
        lev: "Apalancamiento",
        risk: "Riesgo",
        target: "Objetivo",
        scanning: "Escaneando...",
        scanningDesc: "Buscando patrones de COMPRA/VENTA"
      },
      signals: {
        scalpBuy: "SCALP COMPRA",
        smartBuy: "SMART COMPRA",
        trendBuy: "TREND COMPRA",
        scalpSell: "SCALP VENTA",
        smartSell: "SMART VENTA",
        trendSell: "TREND VENTA"
      },
      nav: {
        terminal: "Terminal",
        backtest: "Backtest",
        cabinet: "Panel",
        api: "Claves API",
        website: "Inicio",
      },
      "backtest": {
        "title": "Probador de Estrategias",
        "subtitle": "Verificar la lógica V8.0 con datos históricos",
        "pairLabel": "Par de Trading",
        "periodLabel": "Periodo (Días)",
        "runBtn": "Ejecutar Test",
        "processing": "Procesando...",
        "error": "Error al ejecutar el test. Comprueba tu conexión.",
        
        "info1Title": "Algoritmo V8.0",
        "info1Desc": "Usa datos históricos reales para probar estrategias de RSI, Bollinger y Volumen.",
        "info2Title": "Ejecución Realista",
        "info2Desc": "Simula deslizamiento (0,02%) y comisiones del exchange (0,05%) para resultados justos.",
        "info3Title": "Optimización",
        "info3Desc": "Encuentra los mejores parámetros para scalping o panic trading antes de operar en real.",

        "totalProfit": "Ganancia Total",
        "winRate": "Tasa de Éxito",
        "totalTrades": "Total de Operaciones",
        "drawdown": "Máxima Pérdida",
        "equityCurve": "Curva de Capital",
        "history": "Historial de Operaciones",
        
        "colSignal": "Señal",
        "colType": "Tipo",
        "colEntry": "Entrada",
        "colExit": "Salida",
        "colResult": "Resultado"
      },
      common: {
        loading: "Cargando..."
      },
      profile: {
        title: "Panel Personal",
        subtitle: "Gestión de cuenta y configuración de riesgo",
        logout: "Cerrar sesión",
        connected: "Conectado",
        notLinked: "No vinculado",
        upgradeTitle: "Actualizar a PRO",
        upgradeDesc: "Elimina los límites y obtén acceso al Smart Scanner V8.0.",
        upgradeBtn: "Elegir plan",
        riskTitle: "Gestión de Riesgo",
        riskDesc: "Parámetros de seguridad para tu bot",
        riskPerTrade: "Riesgo por operación (%)",
        riskPerTradeDesc: "% del depósito perdido si se activa el Stop Loss.",
        maxDailyLoss: "Pérdida diaria máxima (%)",
        maxDailyLossDesc: "El bot se detiene si la pérdida diaria supera este valor.",
        maxTrades: "Máx. operaciones simultáneas",
        generalTitle: "Configuración general",
        generalDesc: "Idioma de la interfaz y notificaciones",
        telegramNotif: "Notificaciones de Telegram",
        saveBtn: "Guardar configuración",
        saving: "Guardando...",
        saveSuccess: "¡Configuración guardada correctamente!",
        saveError: "Error al guardar la configuración"
      },
      ai: {
        calibration: {
          title: "Calibración del cerebro IA 🧠",
          message: "Necesito más datos (mínimo 5 operaciones) para encontrar patrones ocultos en tu trading."
        },
        tilt: {
          title: "🛑 TILT DETECTADO",
          message: "Se detectaron 3 pérdidas consecutivas. Las estadísticas muestran un 90% de probabilidad de que la próxima operación falle.",
          action: "Cerrar el terminal por 2 h"
        },
        hold: {
          title: "Síndrome de la « esperanza »",
          message: "Tomas ganancias rápido ({{winTime}} min) pero mantienes pérdidas demasiado tiempo ({{lossTime}} min).",
          action: "Usar stop-loss estricto"
        },
        rr: {
          title: "Trampa matemática",
          message: "La pérdida media (${{loss}}) es mayor que la ganancia media (${{win}}). Depósito en riesgo.",
          action: "Reducir riesgo / Aumentar TP"
        },
        toxic: {
          title: "Vampiro del capital: {{coin}}",
          message: "Perdiste ${{loss}} en {{coin}}. La estrategia falla aquí.",
          action: "Añadir moneda a la lista negra"
        },
        sniper: {
          title: "Modo Francotirador 🎯",
          message: "¡Precisión increíble! WinRate {{winRate}}%. Estás en racha."
        },
        gold: {
          title: "Activo Alpha: {{coin}} 💎",
          message: "Operas {{coin}} perfectamente. Ganancia: +${{profit}}."
        },
        neutral: {
          title: "Vuelo estable",
          message: "WinRate: {{winRate}}%. PnL: ${{pnl}}. No se detectaron anomalías."
        }
      },
      auth: { welcome: "Bienvenido de nuevo", create: "Crear cuenta", loginDesc: "Ingresa al terminal", registerDesc: "Obtén $10,000 demo", email: "Correo", password: "Clave", placeholderEmail: "nombre@ejemplo.com", placeholderPass: "••••••••", loginBtn: "Entrar", registerBtn: "Registrarse", processing: "Procesando...", noAccount: "¿Sin cuenta?", hasAccount: "¿Tienes cuenta?", switchLogin: "Entrar", switchRegister: "Registro", error: "Error del servidor" },
      guide: guideES
    }
  },

  // --- FRENCH (FR) ---
  fr: {
    translation: {
      landing: {
        nav: { howItWorks: "Fonctionnement", features: "Fonctionnalités", "reports": "Analytique", pricing: "Tarifs", faq: "FAQ", login: "Connexion", start: "Commencer", terminal: "Terminal", settings: "Paramètres" },
        hero: { badge: "V6.4: TrendGuard™ AI Activé", title: "Tradez la Crypto", subtitle: "Fonds Algorithmique", desc: "Connectez Binance ou Bybit.", cta: "Lancer Bot", demo: "Démo" },
        stats: { volume: "Volume", traders: "Traders", uptime: "Disponibilité", speed: "Vitesse" },
        how: { title: "Commencer ?", subtitle: "Tout est automatisé.", step1: { title: "Créer compte", desc: "30 secondes." }, step2: { title: "Connecter échange", desc: "Clés API." }, step3: { title: "Choisir stratégie", desc: "'Trend' ou 'Scalp'." } },
        fake: { mode: "MODE TRADING", risk: "RISQUE" },
        features: { title: "Technologie Wall Street", subtitle: "3 algorithmes.", c1: { title: "Multi-Bourse", desc: "Binance et Bybit." }, c2: { title: "TrendGuard™", desc: "Évite les chutes." }, c3: { title: "Smart Entry V2", desc: "Entrées RSI." } },
        pricing: { title: "Tarifs", subtitle: "-20% annuel.", period: "/mois", select: "Choisir", p1: { title: "Start", desc: "Débutant", f1: "1 Bourse", f2: "Protection", f3: "Signaux", f4: "Limite: 5 000 $" }, p2: { title: "Trader", desc: "Populaire", f1: "Binance + Bybit", f2: "TrendGuard™ Pro", f3: "Limite: 20 000 $", f4: "Scalp" }, p3: { title: "Pro", desc: "Max", f1: "Toutes", f2: "Illimité", f3: "VIP", f4: "Accès Anticipé" }, p4: { title: "Investisseur", desc: "Auto", f1: "Marque Blanche", f2: "API", f3: "Manager", f4: "Remise" } },
        contact: { title: "Questions ?", desc: "Réponse rapide.", telegram: "Telegram", email: "Email" },
        faq: { title: "FAQ", subtitle: "Sécurité.", q1: { q: "Sûr ?", a: "Oui, pas de retrait." }, q2: { q: "Gains ?", a: "5-15% mois." }, q3: { q: "Internet ?", a: "Cloud 24/7." }, q4: { q: "Gratuit ?", a: "Oui, Démo." }, q5: { q: "Paires ?", a: "Top 50." } },
        footer: { terms: "Conditions", privacy: "Confidentialité", support: "Support" }
      },
      platform: {
        nav: { dashboard: "Aperçu", history: "Historique", profile: "Profil", billing: "Abonnement", system: "SYSTÈME", settings: "Clés API", guide: "Guide", admin: "Admin", logout: "Déconnexion" },
        risk: { title: "Paramètres", risk: "Risque", leverage: "Levier", tp: "Take Profit", sl: "Stop Loss" },
        btc: { title: "Marché BTC", safe: "SÛR", risk: "RISQUE" },
        actions: { sim: "Simulation (Paper)", real: "Réel (Live)", start: "DÉMARRER", stop: "ARRÊTER", panic: "URGENCE", support: "Chat Support", active: "SYSTÈME ACTIF", paused: "EN PAUSE" },
        dashboard: { title: "Terminal d'Analyse", changeMode: "Changer Mode", live: "Données Live", refresh: "Actualiser", updating: "Mise à jour...", paperTitleFree: "Mode Simulation (Gratuit)", paperTitlePro: "Mode Simulation (Pro)", paperDescFree: "Fonds virtuels.", paperDescPro: "Test en cours.", connectReal: "Compte Réel", virtualBal: "SOLDE VIRTUEL", realBal: "SOLDE RÉEL", pnl: "PnL Total (24h)", deals: "Deals Actifs", winrate: "Taux Réussite", chart: "Analyse", pnlDyn: "Dynamique PnL", heatmap: "Carte Thermique" },
        manualTrade: { title: "Trading Manuel", symbol: "Paire", margin: "Marge (USDT)", leverage: "Levier (x)", positionSize: "Taille Position:", long: "LONG", short: "SHORT", confirm: "Ouvrir {{type}} sur {{symbol}} avec marge ${{amount}} ?", success: "Ordre placé !", error: "Erreur : " },
        logs: { title: "Journaux Système", waiting: "En attente d'événements serveur..." },
        ai: { title: "Analyse IA" },
        telegram: { title: "Gestion Telegram", subtitle: "Liaison sécurisée", step1: "1. Ouvrez", step2: "2. Entrez la commande :", codeLabel: "Code de vérification", copy: "Copier la commande", waiting: "En attente de requête...", active: "Telegram actif" },
        apiSettings: { title: "Paramètres API", success: "Paramètres enregistrés pour", platform: "Choix de la Plateforme", platformDesc: "Choisissez l'échange", keysTitle: "Clés d'accès", keysDesc: "Entrez les clés pour", apiKey: "Clé API", apiSecret: "Clé Secrète", placeholderKey: "Coller la Clé API de", placeholderSecret: "Coller la Clé Secrète de", placeholderSecretHidden: "Clé masquée (entrer nouvelle)", keySaved: "Clé enregistrée.", saveBtn: "ENREGISTRER POUR", saving: "Enregistrement...", current: "Bourse Active:", currentDesc: "Le bot trade sur:", currentKey: "Finit par:" },
        footer: { docs: "Documentation", made: "Fait avec" },
        errors: { chartReload: "Widget en rechargement...", refresh: "Actualiser" },
        history: {
          colAsset: "Actif / Stratégie",
          colDetails: "Détails d’entrée",
          colVolume: "Volume (USDT)",
          colPnl: "PnL & ROI",
          colAction: "Gestion",
          qty: "Quantité",
          trading: "En cours",
          filled: "Exécuté",
          close: "Fermer au marché",
          closing: "Fermeture...",
          emptyTitle: "Le journal des opérations est vide",
          legendSim: "Simulation",
          legendReal: "Compte réel",
          totalOps: "Total des opérations :",
          confirmClose: "Fermer la position {{symbol}} au marché ?",
          closeSuccess: "✅ Position {{symbol}} fermée manuellement",
          error: "Erreur : "
        },
        "strategy": {
          "advancedTitle": "Stratégie avancée",
          "dcaTitle": "DCA (Martingale)",
          "dcaDesc": "Moyennage de position pendant une baisse. Le bot achète davantage pour réduire le prix moyen d’entrée.",
          "dcaStep": "Pas (%)",
          "dcaMult": "Multiplicateur",
          "dcaMax": "Ordres max",
          "trailingTitle": "Take Profit Trailing",
          "trailingDesc": "Permet aux profits de suivre le prix. Le Stop Loss monte lorsque le prix augmente.",
          "trailingAct": "Activation (%)",
          "trailingCall": "Rappel (%)"
        },
        "riskInfo": {
          "balanceUsageDesc": "Le bot n’utilisera pas plus que le pourcentage spécifié du dépôt total.",
          "riskDesc": "Pourcentage du solde risqué sur une seule transaction.",
          "leverageDesc": "Effet de levier pour le trading de contrats à terme.",
          "concurrentDesc": "Nombre maximal de positions ouvertes simultanément.",
          "dailyLossDesc": "Limite de perte quotidienne. Une fois atteinte, le bot s’arrête jusqu’au lendemain.",
          "minVolumeDesc": "Filtre des coins par volume de trading quotidien minimum en USDT."
        },
        "reports": {
          "title": "Analyse de Trading",
          "desc": "Analyse approfondie des performances de votre stratégie de trading",
          "refresh": "Actualiser les données",
          "loading": "Analyse des trades...",
          "profit": "Profit net total",
          "winrate": "Taux de réussite",
          "totalTrades": "Nombre total de trades",
          "activeBans": "Blocages actifs",
          "chartTitle": "Courbe de croissance (PnL quotidien)",
          "performanceTitle": "Performance des actifs",
          "tradesCount": "{{count}} trades",
          "blacklistTitle": "Actifs restreints (Liste noire)",
          "bannedUntil": "Banni jusqu’au",
          "noBans": "Aucun actif n’est actuellement restreint",
          "blockedLabel": "Bloqué"
        }
      },
      rightSidebar: {
        title: "Signaux",
        liveScanner: "Scanner en direct",
        demoBalance: "Solde démo utilisé",
        top: "Top",
        price: "Prix",
        aiScore: "Score IA",
        rsiStrength: "Force RSI",
        lev: "Levier",
        risk: "Risque",
        target: "Objectif",
        scanning: "Analyse...",
        scanningDesc: "Recherche de modèles ACHAT/VENTE"
      },
      signals: {
        scalpBuy: "SCALP ACHAT",
        smartBuy: "SMART ACHAT",
        trendBuy: "TREND ACHAT",
        scalpSell: "SCALP VENTE",
        smartSell: "SMART VENTE",
        trendSell: "TREND VENTE"
      },
      nav: {
        terminal: "Terminal",
        backtest: "Backtest",
        cabinet: "Espace personnel",
        api: "Clés API",
        website: "Accueil",
      },
      "backtest": {
        "title": "Testeur de Stratégies",
        "subtitle": "Vérifiez la logique V8.0 sur des données historiques",
        "pairLabel": "Pair de Trading",
        "periodLabel": "Période (Jours)",
        "runBtn": "Lancer le Test",
        "processing": "Traitement...",
        "error": "Erreur lors du test. Vérifiez votre connexion.",
        
        "info1Title": "Algorithme V8.0",
        "info1Desc": "Utilise des données historiques réelles pour tester les stratégies RSI, Bollinger et Volume.",
        "info2Title": "Exécution Réaliste",
        "info2Desc": "Simule le slippage (0,02%) et les frais de la bourse (0,05%) pour des résultats justes.",
        "info3Title": "Optimisation",
        "info3Desc": "Trouvez les meilleurs paramètres pour le scalping ou le panic trading avant le trading réel.",

        "totalProfit": "Profit Total",
        "winRate": "Taux de Victoire",
        "totalTrades": "Total des Trades",
        "drawdown": "Perte Maximale",
        "equityCurve": "Courbe de Capital",
        "history": "Historique des Trades",
        
        "colSignal": "Signal",
        "colType": "Type",
        "colEntry": "Entrée",
        "colExit": "Sortie",
        "colResult": "Résultat"
      },
      common: {
        loading: "Chargement..."
      },
      profile: {
        title: "Espace Personnel",
        subtitle: "Gestion du compte et paramètres de risque",
        logout: "Se déconnecter",
        connected: "Connecté",
        notLinked: "Non lié",
        upgradeTitle: "Passer à PRO",
        upgradeDesc: "Supprimez les limites et accédez au Smart Scanner V8.0.",
        upgradeBtn: "Choisir un plan",
        riskTitle: "Gestion des risques",
        riskDesc: "Paramètres de sécurité pour votre bot",
        riskPerTrade: "Risque par trade (%)",
        riskPerTradeDesc: "% du dépôt perdu si le Stop Loss est atteint.",
        maxDailyLoss: "Perte quotidienne max (%)",
        maxDailyLossDesc: "Le bot s’arrête si la perte quotidienne dépasse cette valeur.",
        maxTrades: "Trades simultanés max",
        generalTitle: "Paramètres généraux",
        generalDesc: "Langue de l’interface et notifications",
        telegramNotif: "Notifications Telegram",
        saveBtn: "Enregistrer",
        saving: "Enregistrement...",
        saveSuccess: "Paramètres enregistrés avec succès !",
        saveError: "Erreur lors de l’enregistrement des paramètres"
      },
            ai: {
        calibration: {
          title: "Calibration du cerveau IA 🧠",
          message: "J’ai besoin de plus de données (au moins 5 trades) pour détecter des schémas cachés dans ton trading."
        },
        tilt: {
          title: "🛑 TILT DÉTECTÉ",
          message: "3 pertes consécutives détectées. Les statistiques indiquent 90% de chances que le prochain trade échoue.",
          action: "Fermer le terminal pendant 2 h"
        },
        hold: {
          title: "Syndrome de l’« espoir »",
          message: "Tu prends tes profits trop vite ({{winTime}} min) mais gardes tes pertes trop longtemps ({{lossTime}} min).",
          action: "Utilise un stop-loss strict"
        },
        rr: {
          title: "Piège mathématique",
          message: "La perte moyenne (${{loss}}) est supérieure au gain moyen (${{win}}). Capital en danger.",
          action: "Réduire le risque / Augmenter le TP"
        },
        toxic: {
          title: "Vampire du capital : {{coin}}",
          message: "Tu as perdu ${{loss}} sur {{coin}}. La stratégie échoue ici.",
          action: "Mettre la monnaie sur liste noire"
        },
        sniper: {
          title: "Mode Sniper 🎯",
          message: "Précision incroyable ! WinRate {{winRate}}%. Tu es dans le flow."
        },
        gold: {
          title: "Actif Alpha : {{coin}} 💎",
          message: "Tu trades {{coin}} parfaitement. Profit : +${{profit}}."
        },
        neutral: {
          title: "Vol stable",
          message: "WinRate : {{winRate}}%. PnL : ${{pnl}}. Aucune anomalie détectée."
        }
      },
      auth: { welcome: "Bon retour", create: "Créer un compte", loginDesc: "Connexion au terminal", registerDesc: "Obtenez 10 000$ démo", email: "Email", password: "Mot de passe", placeholderEmail: "nom@exemple.com", placeholderPass: "••••••••", loginBtn: "Connexion", registerBtn: "S'inscrire", processing: "Traitement...", noAccount: "Pas de compte ?", hasAccount: "Déjà un compte ?", switchLogin: "Connexion", switchRegister: "S'inscrire", error: "Erreur serveur" },
      guide: guideFR
    }
  },

  // --- CHINESE (ZH) ---
  zh: {
    translation: {
      landing: {
        nav: { howItWorks: "工作原理", features: "功能", pricing: "价格", "reports": "分析", faq: "常见问题", login: "登录", start: "开始", terminal: "终端", settings: "设置" },
        hero: { badge: "V6.4: TrendGuard™ AI 已激活", title: "像算法基金一样", subtitle: "交易加密货币", desc: "2分钟连接Binance或Bybit。", cta: "启动机器人", demo: "现场演示" },
        stats: { volume: "交易量", traders: "活跃交易者", uptime: "运行时间", speed: "速度" },
        how: { title: "如何开始？", subtitle: "全自动。", step1: { title: "创建账户", desc: "30秒。" }, step2: { title: "连接交易所", desc: "输入API密钥。" }, step3: { title: "选择策略", desc: "'趋势'或'剥头皮'。" } },
        fake: { mode: "交易模式", risk: "风险" },
        features: { title: "华尔街技术", subtitle: "强大算法。", c1: { title: "多交易所", desc: "Binance和Bybit。" }, c2: { title: "TrendGuard™", desc: "防止亏损。" }, c3: { title: "智能入场 V2", desc: "RSI入场。" } },
        pricing: { title: "价格", subtitle: "年付省20%。", period: "/月", select: "选择", p1: { title: "入门", desc: "新手", f1: "1个交易所", f2: "基础保护", f3: "信号", f4: "额度: $5,000" }, p2: { title: "交易员", desc: "热门", f1: "Binance + Bybit", f2: "TrendGuard™ Pro", f3: "额度: $20,000", f4: "剥头皮" }, p3: { title: "专业", desc: "最大", f1: "所有", f2: "无限", f3: "VIP", f4: "抢先" }, p4: { title: "投资者", desc: "全自动", f1: "白标", f2: "API", f3: "经理", f4: "折扣" } },
        contact: { title: "有问题？", desc: "快速回复。", telegram: "Telegram", email: "邮件" },
        faq: { title: "常见问题", subtitle: "安全。", q1: { q: "安全吗？", a: "是的，无提款权。" }, q2: { q: "收益？", a: "月5-15%。" }, q3: { q: "断网？", a: "云端运行。" }, q4: { q: "免费？", a: "是的，模拟。" }, q5: { q: "币种？", a: "前50名。" } },
        footer: { terms: "条款", privacy: "隐私", support: "支持" }
      },
      platform: {
        nav: { dashboard: "概览", history: "历史", profile: "个人资料", billing: "订阅", system: "系统", settings: "API 密钥", guide: "指南", admin: "管理", logout: "登出" },
        risk: { title: "风险参数", risk: "风险", leverage: "杠杆", tp: "止盈", sl: "止损" },
        btc: { title: "BTC 市场", safe: "安全", risk: "风险" },
        actions: { sim: "模拟 (Paper)", real: "实盘 (Live)", start: "启动系统", stop: "停止", panic: "紧急平仓", support: "支持聊天", active: "系统运行中", paused: "系统暂停" },
        dashboard: { title: "分析终端", changeMode: "更改模式", live: "实时数据", refresh: "刷新", updating: "更新中...", paperTitleFree: "模拟模式 (免费)", paperTitlePro: "模拟模式 (专业)", paperDescFree: "虚拟资金交易。", paperDescPro: "测试模式。", connectReal: "连接实盘", virtualBal: "虚拟余额", realBal: "交易所余额", pnl: "总盈亏 (24h)", deals: "活跃订单", winrate: "胜率", chart: "图表分析", pnlDyn: "盈亏动态", heatmap: "市场热力图" },
        manualTrade: { title: "手动交易", symbol: "交易对", margin: "保证金 (USDT)", leverage: "杠杆 (x)", positionSize: "仓位大小:", long: "做多", short: "做空", confirm: "以保证金 ${{amount}} 在 {{symbol}} 开 {{type}}？", success: "下单成功！", error: "错误: " },
        logs: { title: "系统实时日志", waiting: "等待服务器事件..." },
        ai: { title: "AI 分析" },
        telegram: { title: "Telegram 管理", subtitle: "安全账户绑定", step1: "1. 打开", step2: "2. 输入命令：", codeLabel: "验证码", copy: "复制命令", waiting: "等待 Telegram 请求...", active: "Telegram 已激活" },
        apiSettings: { title: "API 设置", success: "设置已保存", platform: "平台选择", platformDesc: "选择交易所配置密钥", keysTitle: "访问密钥", keysDesc: "输入密钥", apiKey: "API Key", apiSecret: "Secret Key", placeholderKey: "粘贴 API Key 来自", placeholderSecret: "粘贴 Secret Key 来自", placeholderSecretHidden: "密钥已隐藏 (输入新密钥以替换)", keySaved: "密钥已保存。", saveBtn: "保存", saving: "保存中...", current: "当前交易所:", currentDesc: "机器人运行于:", currentKey: "结尾为:" },
        footer: { docs: "文档", made: "制作于" },
        errors: { chartReload: "图表重新加载中...", refresh: "刷新" },
        history: {
          colAsset: "资产 / 策略",
          colDetails: "入场详情",
          colVolume: "数量 (USDT)",
          colPnl: "盈亏 & ROI",
          colAction: "操作",
          qty: "数量",
          trading: "交易中",
          filled: "已成交",
          close: "市价平仓",
          closing: "平仓中...",
          emptyTitle: "交易记录为空",
          legendSim: "模拟",
          legendReal: "真实账户",
          totalOps: "总操作数：",
          confirmClose: "以市价平仓 {{symbol}}？",
          closeSuccess: "✅ {{symbol}} 仓位已手动平仓",
          error: "错误："
        },
        "strategy": {
          "advancedTitle": "高级策略",
          "dcaTitle": "DCA（马丁格尔）",
          "dcaDesc": "在回撤期间进行仓位均摊。机器人会继续买入以降低平均入场价格。",
          "dcaStep": "步长 (%)",
          "dcaMult": "倍数",
          "dcaMax": "最大订单数",
          "trailingTitle": "追踪止盈",
          "trailingDesc": "让利润随着价格上涨而增长。当价格上涨时，止损也会随之上移。",
          "trailingAct": "激活 (%)",
          "trailingCall": "回调 (%)"
        },
        "riskInfo": {
          "balanceUsageDesc": "机器人使用的资金不超过总存款的指定百分比。",
          "riskDesc": "单笔交易所承担的账户风险百分比。",
          "leverageDesc": "期货交易使用的杠杆。",
          "concurrentDesc": "同时持有的最大仓位数量。",
          "dailyLossDesc": "每日亏损限制。达到后，机器人将停止交易直到第二天。",
          "minVolumeDesc": "按每日最低 USDT 交易量筛选币种。"
        },
        "reports": {
          "title": "交易分析",
          "desc": "对您的交易策略表现进行深入分析",
          "refresh": "刷新数据",
          "loading": "正在分析交易...",
          "profit": "总净利润",
          "winrate": "胜率",
          "totalTrades": "交易总数",
          "activeBans": "活跃封禁",
          "chartTitle": "增长曲线（每日盈亏）",
          "performanceTitle": "资产表现",
          "tradesCount": "{{count}} 笔交易",
          "blacklistTitle": "受限资产（黑名单）",
          "bannedUntil": "封禁至",
          "noBans": "当前没有受限资产",
          "blockedLabel": "已封禁"
        }
      },
      rightSidebar: {
        title: "信号",
        liveScanner: "实时扫描",
        demoBalance: "已使用模拟余额",
        top: "排行",
        price: "价格",
        aiScore: "AI 评分",
        rsiStrength: "RSI 强度",
        lev: "杠杆",
        risk: "风险",
        target: "目标",
        scanning: "扫描中...",
        scanningDesc: "正在搜索 买入/卖出 模式"
      },
      signals: {
        scalpBuy: "剥头皮买入",
        smartBuy: "智能买入",
        trendBuy: "趋势买入",
        scalpSell: "剥头皮卖出",
        smartSell: "智能卖出",
        trendSell: "趋势卖出"
      },
      nav: {
        terminal: "交易终端",
        backtest: "回测",
        cabinet: "个人中心",
        api: "API 密钥",
        website: "主页"
      },
      "backtest": {
        "title": "策略测试器",
        "subtitle": "在历史数据上验证 V8.0 逻辑",
        "pairLabel": "交易对",
        "periodLabel": "周期（天）",
        "runBtn": "开始测试",
        "processing": "处理中...",
        "error": "测试运行出错，请检查连接。",
        
        "info1Title": "V8.0 算法",
        "info1Desc": "使用真实历史数据测试 RSI、布林带和成交量策略。",
        "info2Title": "真实执行",
        "info2Desc": "模拟滑点（0.02%）和交易所手续费（0.05%），保证结果公平。",
        "info3Title": "优化",
        "info3Desc": "在实盘前找到适合剥头皮或恐慌交易的最佳参数。",

        "totalProfit": "总收益",
        "winRate": "胜率",
        "totalTrades": "总交易数",
        "drawdown": "最大回撤",
        "equityCurve": "资金曲线",
        "history": "交易历史",
        
        "colSignal": "信号",
        "colType": "类型",
        "colEntry": "入场",
        "colExit": "出场",
        "colResult": "结果"
      },
      common: {
        loading: "加载中..."
      },
      profile: {
        title: "个人中心",
        subtitle: "账户管理与风险设置",
        logout: "退出登录",
        connected: "已连接",
        notLinked: "未连接",
        upgradeTitle: "升级至 PRO",
        upgradeDesc: "解除限制并获取 Smart Scanner V8.0 访问权限。",
        upgradeBtn: "选择套餐",
        riskTitle: "风险管理",
        riskDesc: "机器人安全参数",
        riskPerTrade: "单笔交易风险 (%)",
        riskPerTradeDesc: "触发止损时损失的资金比例。",
        maxDailyLoss: "每日最大亏损 (%)",
        maxDailyLossDesc: "当每日亏损超过该值时机器人将停止。",
        maxTrades: "最大同时交易数",
        generalTitle: "通用设置",
        generalDesc: "界面语言和通知",
        telegramNotif: "Telegram 通知",
        saveBtn: "保存设置",
        saving: "保存中...",
        saveSuccess: "设置保存成功！",
        saveError: "保存设置时出错"
      },
      ai: {
          calibration: {
            title: "AI 大脑校准 🧠",
            message: "我需要更多数据（至少 5 笔交易）来发现你交易中的隐藏模式。"
          },
          tilt: {
            title: "🛑 检测到情绪失控",
            message: "检测到连续 3 笔亏损交易。统计显示下一笔交易失败的概率为 90%。",
            action: "关闭终端 2 小时"
          },
          hold: {
            title: "「希望」综合症",
            message: "你过快止盈（{{winTime}} 分钟），却长期持有亏损（{{lossTime}} 分钟）。",
            action: "使用严格止损"
          },
          rr: {
            title: "数学陷阱",
            message: "平均亏损 (${{loss}}) 大于平均盈利 (${{win}})，资金面临风险。",
            action: "降低风险 / 提高止盈"
          },
          toxic: {
            title: "资金吸血鬼：{{coin}}",
            message: "你在 {{coin}} 上亏损了 ${{loss}}。该策略在此失效。",
            action: "将币种加入黑名单"
          },
          sniper: {
            title: "狙击手模式 🎯",
            message: "惊人的精准度！胜率 {{winRate}}%。你正处于巅峰状态。"
          },
          gold: {
            title: "α 资产：{{coin}} 💎",
            message: "你完美地交易 {{coin}}。盈利：+${{profit}}。"
          },
          neutral: {
            title: "稳定飞行",
            message: "胜率：{{winRate}}%。PnL：${{pnl}}。未检测到异常。"
          }
        },
      auth: { welcome: "欢迎回来", create: "创建账户", loginDesc: "登录交易终端", registerDesc: "获得 $10,000 模拟金", email: "邮箱", password: "密码", placeholderEmail: "name@example.com", placeholderPass: "••••••••", loginBtn: "登录", registerBtn: "注册", processing: "处理中...", noAccount: "没有账户？", hasAccount: "已有账户？", switchLogin: "登录", switchRegister: "注册", error: "服务器错误" },
      guide: guideZH
    }
  },

  // --- JAPANESE (JA) ---
  ja: {
    translation: {
      landing: {
        nav: { howItWorks: "仕組み", features: "特徴", pricing: "価格", "reports": "分析", faq: "FAQ", login: "ログイン", start: "開始", terminal: "ターミナル", settings: "設定" },
        hero: { badge: "V6.4: TrendGuard™ AI 有効", title: "アルゴリズムファンドのように", subtitle: "仮想通貨取引", desc: "BinanceまたはBybitを接続。", cta: "ボット起動", demo: "ライブデモ" },
        stats: { volume: "取引量", traders: "トレーダー", uptime: "稼働時間", speed: "速度" },
        how: { title: "始め方", subtitle: "全自動。", step1: { title: "登録", desc: "30秒。" }, step2: { title: "接続", desc: "APIキー。" }, step3: { title: "戦略", desc: "トレンドまたはスキャルプ。" } },
        fake: { mode: "モード", risk: "リスク" },
        features: { title: "技術", subtitle: "アルゴリズム。", c1: { title: "マルチ取引所", desc: "同時取引。" }, c2: { title: "TrendGuard™", desc: "下落防止。" }, c3: { title: "スマートエントリー", desc: "RSI。" } },
        pricing: { title: "価格", subtitle: "20%割引。", period: "/月", select: "選択", p1: { title: "開始", desc: "初心者", f1: "1 取引所", f2: "保護", f3: "シグナル", f4: "限度: $5,000" }, p2: { title: "トレーダー", desc: "人気", f1: "Binance + Bybit", f2: "TrendGuard™ Pro", f3: "限度: $20,000", f4: "スキャルプ" }, p3: { title: "プロ", desc: "最大", f1: "全取引所", f2: "無制限", f3: "VIP", f4: "早期" }, p4: { title: "投資家", desc: "自動", f1: "ホワイトラベル", f2: "API", f3: "担当", f4: "割引" } },
        contact: { title: "質問？", desc: "サポート。", telegram: "Telegram", email: "Email" },
        faq: { title: "FAQ", subtitle: "セキュリティ。", q1: { q: "安全？", a: "はい。出金不可。" }, q2: { q: "利益？", a: "月5-15%。" }, q3: { q: "ネット？", a: "クラウド。" }, q4: { q: "無料？", a: "はい。" }, q5: { q: "通貨？", a: "トップ50。" } },
        footer: { terms: "利用規約", privacy: "プライバシー", support: "サポート" }
      },
      platform: {
        nav: { dashboard: "概要", history: "履歴", profile: "プロフィール", billing: "サブスク", system: "システム", settings: "APIキー", guide: "ガイド", admin: "管理", logout: "ログアウト" },
        risk: { title: "リスク設定", risk: "リスク", leverage: "レバレッジ", tp: "利食い", sl: "損切り" },
        btc: { title: "BTC市場", safe: "安全", risk: "危険" },
        actions: { sim: "シミュレーション", real: "リアル (Live)", start: "システム起動", stop: "停止", panic: "緊急決済", support: "チャット", active: "稼働中", paused: "一時停止" },
        dashboard: { title: "分析ターミナル", changeMode: "モード変更", live: "ライブ", refresh: "更新", updating: "更新中...", paperTitleFree: "デモモード (無料)", paperTitlePro: "デモモード (Pro)", paperDescFree: "仮想資金です。", paperDescPro: "テスト中。", connectReal: "リアル口座接続", virtualBal: "仮想残高", realBal: "リアル残高", pnl: "損益合計 (24h)", deals: "取引数", winrate: "勝率", chart: "チャート分析", pnlDyn: "損益推移", heatmap: "ヒートマップ" },
        manualTrade: { title: "手動取引", symbol: "取引ペア", margin: "証拠金 (USDT)", leverage: "レバレッジ (x)", positionSize: "ポジションサイズ:", long: "買い (LONG)", short: "売り (SHORT)", confirm: "{{symbol}}で証拠金${{amount}}の{{type}}をオープンしますか？", success: "注文が正常に送信されました！", error: "エラー: " },
        logs: { title: "システムログ", waiting: "サーバーイベントを待機中..." },
        ai: { title: "AI分析" },
        telegram: { title: "Telegram管理", subtitle: "安全なアカウント連携", step1: "1. 開く", step2: "2. コマンドを入力:", codeLabel: "確認コード", copy: "コマンドをコピー", waiting: "Telegram待機中...", active: "Telegram有効" },
        apiSettings: { title: "API設定", success: "設定を保存しました", platform: "プラットフォーム選択", platformDesc: "取引所を選択", keysTitle: "アクセスキー", keysDesc: "キーを入力", apiKey: "APIキー", apiSecret: "シークレットキー", placeholderKey: "APIキーを貼り付け", placeholderSecret: "シークレットキーを貼り付け", placeholderSecretHidden: "キーは非表示です（置換するには新規入力）", keySaved: "キーは保存されています。", saveBtn: "保存", saving: "保存中...", current: "アクティブな取引所:", currentDesc: "現在の取引先:", currentKey: "末尾:" },
        footer: { docs: "ドキュメント", made: "作成者" },
        errors: { chartReload: "ウィジェットをリロード中...", refresh: "更新" },
        history: {
          colAsset: "資産 / 戦略",
          colDetails: "エントリー詳細",
          colVolume: "数量 (USDT)",
          colPnl: "損益 & ROI",
          colAction: "操作",
          qty: "数量",
          trading: "取引中",
          filled: "約定済み",
          close: "成行でクローズ",
          closing: "クローズ中...",
          emptyTitle: "取引履歴が空です",
          legendSim: "シミュレーション",
          legendReal: "リアル口座",
          totalOps: "総取引数：",
          confirmClose: "{{symbol}} のポジションを成行でクローズしますか？",
          closeSuccess: "✅ {{symbol}} のポジションを手動でクローズしました",
          error: "エラー："
        },
        "strategy": {
          "advancedTitle": "高度な戦略",
          "dcaTitle": "DCA（マーチンゲール）",
          "dcaDesc": "下落時にポジションを平均化します。ボットが追加購入して平均エントリー価格を下げます。",
          "dcaStep": "ステップ (%)",
          "dcaMult": "倍率",
          "dcaMax": "最大注文数",
          "trailingTitle": "トレーリング利確",
          "trailingDesc": "価格に追従して利益を拡大します。価格上昇に応じてストップロスが引き上げられます。",
          "trailingAct": "有効化 (%)",
          "trailingCall": "コールバック (%)"
        },
        "riskInfo": {
          "balanceUsageDesc": "ボットは総資金の指定された割合までしか使用しません。",
          "riskDesc": "1回の取引でリスクにさらす残高の割合。",
          "leverageDesc": "先物取引のためのレバレッジ。",
          "concurrentDesc": "同時に開くことができる最大ポジション数。",
          "dailyLossDesc": "1日の損失制限。到達すると翌日まで取引を停止します。",
          "minVolumeDesc": "USDT建ての1日最小取引量による通貨フィルター。"
        },
        "reports": {
          "title": "取引分析",
          "desc": "取引戦略のパフォーマンスを詳細に分析します",
          "refresh": "データ更新",
          "loading": "取引を分析中...",
          "profit": "純利益合計",
          "winrate": "勝率",
          "totalTrades": "総取引数",
          "activeBans": "有効な制限",
          "chartTitle": "成長曲線（日次PnL）",
          "performanceTitle": "資産パフォーマンス",
          "tradesCount": "{{count}} 件の取引",
          "blacklistTitle": "制限された資産（ブラックリスト）",
          "bannedUntil": "制限解除日",
          "noBans": "現在制限されている資産はありません",
          "blockedLabel": "ブロック済み"
        }
      },
      rightSidebar: {
        title: "シグナル",
        liveScanner: "ライブスキャナー",
        demoBalance: "使用中のデモ残高",
        top: "トップ",
        price: "価格",
        aiScore: "AIスコア",
        rsiStrength: "RSI強度",
        lev: "レバレッジ",
        risk: "リスク",
        target: "目標",
        scanning: "スキャン中...",
        scanningDesc: "買い/売り パターンを検索中"
      },
      signals: {
        scalpBuy: "スキャルプ買い",
        smartBuy: "スマート買い",
        trendBuy: "トレンド買い",
        scalpSell: "スキャルプ売り",
        smartSell: "スマート売り",
        trendSell: "トレンド売り"
      },
      nav: {
        terminal: "取引ターミナル",
        backtest: "バックテスト",
        cabinet: "アカウント",
        api: "APIキー",
        website: "ホーム"
      },
      "backtest": {
        "title": "ストラテジーテスター",
        "subtitle": "過去データでV8.0ロジックを検証",
        "pairLabel": "取引ペア",
        "periodLabel": "期間（日）",
        "runBtn": "テスト開始",
        "processing": "処理中...",
        "error": "テスト実行エラー。接続を確認してください。",
        
        "info1Title": "V8.0 アルゴリズム",
        "info1Desc": "実際の過去データを使ってRSI、ボリンジャー、ボリューム戦略を検証します。",
        "info2Title": "リアルな実行",
        "info2Desc": "スリッページ（0.02%）と取引所手数料（0.05%）をシミュレーションして公正な結果を提供します。",
        "info3Title": "最適化",
        "info3Desc": "実運用前にスキャルピングやパニック取引の最適パラメータを見つけます。",

        "totalProfit": "総利益",
        "winRate": "勝率",
        "totalTrades": "総取引数",
        "drawdown": "最大ドローダウン",
        "equityCurve": "資金曲線",
        "history": "取引履歴",
        
        "colSignal": "シグナル",
        "colType": "タイプ",
        "colEntry": "エントリー",
        "colExit": "エグジット",
        "colResult": "結果"
      },
      common: {
        loading: "読み込み中..."
      },
      profile: {
        title: "個人キャビネット",
        subtitle: "アカウント管理とリスク設定",
        logout: "ログアウト",
        connected: "接続済み",
        notLinked: "未接続",
        upgradeTitle: "PRO にアップグレード",
        upgradeDesc: "制限を解除し、Smart Scanner V8.0 にアクセスできます。",
        upgradeBtn: "プランを選択",
        riskTitle: "リスク管理",
        riskDesc: "ボットのセキュリティ設定",
        riskPerTrade: "取引ごとのリスク (%)",
        riskPerTradeDesc: "ストップロス到達時に失われる資金の割合。",
        maxDailyLoss: "1日の最大損失 (%)",
        maxDailyLossDesc: "1日の損失がこの値を超えるとボットは停止します。",
        maxTrades: "最大同時取引数",
        generalTitle: "一般設定",
        generalDesc: "インターフェース言語と通知",
        telegramNotif: "Telegram 通知",
        saveBtn: "設定を保存",
        saving: "保存中...",
        saveSuccess: "設定が正常に保存されました！",
        saveError: "設定の保存中にエラーが発生しました"
      },
            ai: {
        calibration: {
          title: "AI脳のキャリブレーション 🧠",
          message: "取引の隠れたパターンを見つけるには、最低5回の取引データが必要です。"
        },
        tilt: {
          title: "🛑 ティルト検出",
          message: "3回連続の損失が検出されました。次の取引が失敗する確率は90%です。",
          action: "2時間ターミナルを閉じる"
        },
        hold: {
          title: "「希望」症候群",
          message: "利益は早く確定（{{winTime}}分）しますが、損失は長く保持（{{lossTime}}分）しています。",
          action: "厳格なストップロスを使用"
        },
        rr: {
          title: "数学的トラップ",
          message: "平均損失 (${{loss}}) が平均利益 (${{win}}) を上回っています。資金が危険です。",
          action: "リスクを下げる / 利確を増やす"
        },
        toxic: {
          title: "資金の吸血鬼：{{coin}}",
          message: "{{coin}} で ${{loss}} を失いました。この戦略は通用しません。",
          action: "ブラックリストに追加"
        },
        sniper: {
          title: "スナイパーモード 🎯",
          message: "驚異的な精度！勝率 {{winRate}}%。最高の状態です。"
        },
        neutral: {
          title: "安定飛行",
          message: "勝率：{{winRate}}%。PnL：${{pnl}}。異常は検出されませんでした。"
        },
        gold: {
        title: "アルファ資産：{{coin}} 💎",
        message: "{{coin}} を完璧に取引しています。利益：+${{profit}}。"
      },
    },
      auth: { welcome: "おかえりなさい", create: "アカウント作成", loginDesc: "ターミナルにログイン", registerDesc: "$10,000のデモ資金", email: "メール", password: "パスワード", placeholderEmail: "name@example.com", placeholderPass: "••••••••", loginBtn: "ログイン", registerBtn: "登録", processing: "処理中...", noAccount: "アカウントなし？", hasAccount: "アカウントをお持ちですか？", switchLogin: "ログイン", switchRegister: "登録", error: "サーバーエラー" },
      guide: guideJA
    }
  },

  // --- KOREAN (KO) ---
  ko: {
    translation: {
      landing: {
        nav: { howItWorks: "작동 원리", features: "기능", pricing: "가격", "reports": "분석", faq: "FAQ", login: "로그인", start: "시작", terminal: "터미널", settings: "설정" },
        hero: { badge: "V6.4: TrendGuard™ AI 활성", title: "알고리즘 펀드처럼", subtitle: "암호화폐 거래", desc: "Binance 또는 Bybit 연결。", cta: "봇 시작", demo: "라이브 데모" },
        stats: { volume: "거래량", traders: "트레이더", uptime: "가동 시간", speed: "속도" },
        how: { title: "시작 방법", subtitle: "자동화。", step1: { title: "계정", desc: "30초。" }, step2: { title: "연결", desc: "API 키。" }, step3: { title: "전략", desc: "트렌드 또는 스캘핑。" } },
        fake: { mode: "모드", risk: "리스크" },
        features: { title: "기술", subtitle: "알고리즘。", c1: { title: "멀티 거래소", desc: "동시 거래。" }, c2: { title: "TrendGuard™", desc: "하락 방지。" }, c3: { title: "스마트 진입", desc: "RSI。" } },
        pricing: { title: "가격", subtitle: "20% 할인。", period: "/월", select: "선택", p1: { title: "시작", desc: "초보자", f1: "1 거래소", f2: "보호", f3: "신호", f4: "한도: $5,000" }, p2: { title: "트레이더", desc: "인기", f1: "Binance + Bybit", f2: "TrendGuard™ Pro", f3: "한도: $20,000", f4: "스캘핑" }, p3: { title: "프로", desc: "최대", f1: "모든", f2: "무제한", f3: "VIP", f4: "조기" }, p4: { title: "투자자", desc: "자동", f1: "화이트 라벨", f2: "API", f3: "매니저", f4: "할인" } },
        contact: { title: "질문?", desc: "지원。", telegram: "Telegram", email: "이메일" },
        faq: { title: "FAQ", subtitle: "보안。", q1: { q: "안전?", a: "네. 출금 불가。" }, q2: { q: "수익?", a: "월 5-15%。" }, q3: { q: "인터넷?", a: "클라우드。" }, q4: { q: "무료?", a: "네。" }, q5: { q: "코인?", a: "상위 50。" } },
        footer: { terms: "이용 약관", privacy: "개인정보", support: "지원" }
      },
      platform: {
        nav: { dashboard: "대시보드", history: "내역", profile: "프로필", billing: "구독", system: "시스템", settings: "API 키", guide: "가이드", admin: "관리자", logout: "로그아웃" },
        risk: { title: "리스크 설정", risk: "리스크", leverage: "레버리지", tp: "이익 실현", sl: "손절매" },
        btc: { title: "BTC 시장", safe: "안전", risk: "위험" },
        actions: { sim: "시뮬레이션", real: "실제 (Live)", start: "시스템 시작", stop: "중지", panic: "긴급 종료", support: "지원 채팅", active: "시스템 활성", paused: "일시 중지" },
        dashboard: { title: "분석 터미널", changeMode: "모드 변경", live: "실시간", refresh: "새로고침", updating: "업데이트...", paperTitleFree: "시뮬레이션 (무료)", paperTitlePro: "시뮬레이션 (Pro)", paperDescFree: "가상 자금입니다.", paperDescPro: "테스트 중입니다.", connectReal: "실제 계정 연결", virtualBal: "가상 잔액", realBal: "실제 잔액", pnl: "총 손익 (24h)", deals: "활성 거래", winrate: "승률", chart: "차트 분석", pnlDyn: "손익 추이", heatmap: "히트맵" },
        manualTrade: { title: "수동 거래", symbol: "거래 쌍", margin: "마진 (USDT)", leverage: "레버리지 (x)", positionSize: "포지션 크기:", long: "롱 (LONG)", short: "숏 (SHORT)", confirm: "{{symbol}}에 증거금 ${{amount}}로 {{type}} 포지션을 오픈하시겠습니까?", success: "주문이 성공적으로 전송되었습니다!", error: "오류: " },
        logs: { title: "시스템 로그", waiting: "서버 이벤트 대기 중..." },
        ai: { title: "AI 분석" },
        telegram: { title: "Telegram 관리", subtitle: "안전한 계정 연동", step1: "1. 열기", step2: "2. 명령어 입력:", codeLabel: "인증 코드", copy: "명령어 복사", waiting: "요청 대기 중...", active: "Telegram 활성" },
        apiSettings: { title: "API 설정", success: "설정 저장됨", platform: "플랫폼 선택", platformDesc: "키를 구성할 거래소 선택", keysTitle: "액세스 키", keysDesc: "키 입력", apiKey: "API Key", apiSecret: "Secret Key", placeholderKey: "API Key 붙여넣기", placeholderSecret: "Secret Key 붙여넣기", placeholderSecretHidden: "키 숨겨짐 (교체하려면 입력)", keySaved: "키가 저장되었습니다.", saveBtn: "저장", saving: "저장 중...", current: "활성 거래소:", currentDesc: "현재 거래 중:", currentKey: "끝자리:" },
        footer: { docs: "문서", made: "제작" },
        errors: { chartReload: "위젯 다시 로드 중...", refresh: "새로고침" },
        history: {
          colAsset: "자산 / 전략",
          colDetails: "진입 세부 정보",
          colVolume: "수량 (USDT)",
          colPnl: "PnL & ROI",
          colAction: "관리",
          qty: "수량",
          trading: "거래 중",
          filled: "체결됨",
          close: "시장가 종료",
          closing: "종료 중...",
          emptyTitle: "거래 내역이 비어 있습니다",
          legendSim: "시뮬레이션",
          legendReal: "실계좌",
          totalOps: "총 거래 수:",
          confirmClose: "{{symbol}} 포지션을 시장가로 종료할까요?",
          closeSuccess: "✅ {{symbol}} 포지션이 수동으로 종료되었습니다",
          error: "오류: "
        },
         "strategy": {
          "advancedTitle": "고급 전략",
          "dcaTitle": "DCA (마틴게일)",
          "dcaDesc": "하락 구간에서 포지션을 평균화합니다. 봇이 추가 매수하여 평균 진입가를 낮춥니다.",
          "dcaStep": "단계 (%)",
          "dcaMult": "배수",
          "dcaMax": "최대 주문 수",
          "trailingTitle": "트레일링 익절",
          "trailingDesc": "가격을 따라 수익을 극대화합니다. 가격이 상승하면 손절선도 함께 올라갑니다.",
          "trailingAct": "활성화 (%)",
          "trailingCall": "콜백 (%)"
        },
        "riskInfo": {
          "balanceUsageDesc": "봇은 총 예치금의 지정된 비율을 초과하여 사용하지 않습니다.",
          "riskDesc": "단일 거래에서 감수하는 잔고 비율.",
          "leverageDesc": "선물 거래를 위한 레버리지.",
          "concurrentDesc": "동시에 열 수 있는 최대 포지션 수.",
          "dailyLossDesc": "일일 손실 한도. 도달 시 다음 날까지 거래가 중단됩니다.",
          "minVolumeDesc": "USDT 기준 최소 일일 거래량으로 코인을 필터링합니다."
        },
        "reports": {
          "title": "트레이딩 분석",
          "desc": "트레이딩 전략 성과에 대한 심층 분석",
          "refresh": "데이터 새로고침",
          "loading": "거래 분석 중...",
          "profit": "총 순이익",
          "winrate": "승률",
          "totalTrades": "총 거래 수",
          "activeBans": "활성 제한",
          "chartTitle": "성장 곡선 (일일 PnL)",
          "performanceTitle": "자산 성과",
          "tradesCount": "{{count}}건의 거래",
          "blacklistTitle": "제한된 자산 (블랙리스트)",
          "bannedUntil": "차단 종료",
          "noBans": "현재 제한된 자산이 없습니다",
          "blockedLabel": "차단됨"
        }
      },
      rightSidebar: {
        title: "신호",
        liveScanner: "실시간 스캐너",
        demoBalance: "사용된 데모 잔액",
        top: "상위",
        price: "가격",
        aiScore: "AI 점수",
        rsiStrength: "RSI 강도",
        lev: "레버리지",
        risk: "위험",
        target: "목표",
        scanning: "스캔 중...",
        scanningDesc: "매수/매도 패턴 검색 중"
      },
      signals: {
        scalpBuy: "스캘핑 매수",
        smartBuy: "스마트 매수",
        trendBuy: "트렌드 매수",
        scalpSell: "스캘핑 매도",
        smartSell: "스마트 매도",
        trendSell: "트렌드 매도"
      },
      nav: {
        terminal: "트레이딩 터미널",
        backtest: "백테스트",
        cabinet: "계정",
        api: "API 키",
        website: "홈"
      },
      "backtest": {
        "title": "전략 테스터",
        "subtitle": "과거 데이터를 통한 V8.0 로직 검증",
        "pairLabel": "거래 페어",
        "periodLabel": "기간 (일)",
        "runBtn": "테스트 실행",
        "processing": "처리 중...",
        "error": "테스트 실행 오류. 연결을 확인하세요.",
        
        "info1Title": "V8.0 알고리즘",
        "info1Desc": "실제 과거 데이터를 사용하여 RSI, 볼린저, 거래량 전략을 테스트합니다.",
        "info2Title": "실제 실행",
        "info2Desc": "슬리피지(0.02%)와 거래소 수수료(0.05%)를 시뮬레이션하여 공정한 결과 제공.",
        "info3Title": "최적화",
        "info3Desc": "실거래 전 스캘핑 또는 패닉 트레이딩에 최적의 파라미터를 찾습니다.",

        "totalProfit": "총 수익",
        "winRate": "승률",
        "totalTrades": "총 거래 수",
        "drawdown": "최대 손실",
        "equityCurve": "자본 곡선",
        "history": "거래 기록",
        
        "colSignal": "신호",
        "colType": "유형",
        "colEntry": "진입",
        "colExit": "청산",
        "colResult": "결과"
      },
      common: {
        loading: "로딩 중..."
      },
      profile: {
        title: "개인 캐비닛",
        subtitle: "계정 관리 및 리스크 설정",
        logout: "로그아웃",
        connected: "연결됨",
        notLinked: "연결되지 않음",
        upgradeTitle: "PRO로 업그레이드",
        upgradeDesc: "제한을 제거하고 Smart Scanner V8.0에 액세스하세요.",
        upgradeBtn: "플랜 선택",
        riskTitle: "리스크 관리",
        riskDesc: "봇 보안 설정",
        riskPerTrade: "거래당 리스크 (%)",
        riskPerTradeDesc: "손절가 도달 시 손실되는 자금 비율.",
        maxDailyLoss: "일일 최대 손실 (%)",
        maxDailyLossDesc: "일일 손실이 이 값을 초과하면 봇이 중지됩니다.",
        maxTrades: "최대 동시 거래 수",
        generalTitle: "일반 설정",
        generalDesc: "인터페이스 언어 및 알림",
        telegramNotif: "Telegram 알림",
        saveBtn: "설정 저장",
        saving: "저장 중...",
        saveSuccess: "설정이 성공적으로 저장되었습니다!",
        saveError: "설정 저장 중 오류 발생"
      },
      ai: {
          calibration: {
            title: "AI 브레인 보정 🧠",
            message: "거래에서 숨겨진 패턴을 찾으려면 최소 5건의 거래 데이터가 필요합니다."
          },
          tilt: {
            title: "🛑 틸트 감지",
            message: "연속 3회 손실이 감지되었습니다. 다음 거래가 실패할 확률은 90%입니다.",
            action: "2시간 동안 터미널 종료"
          },
          hold: {
            title: "「희망」 증후군",
            message: "수익은 빠르게 확정하지만 ({{winTime}}분), 손실은 너무 오래 유지합니다 ({{lossTime}}분).",
            action: "강력한 손절 사용"
          },
          rr: {
            title: "수학적 함정",
            message: "평균 손실 (${{loss}}) 이 평균 수익 (${{win}}) 보다 큽니다. 자본이 위험합니다.",
            action: "리스크 감소 / TP 증가"
          },
          toxic: {
            title: "자본 흡혈귀: {{coin}}",
            message: "{{coin}} 에서 ${{loss}} 를 잃었습니다. 전략이 통하지 않습니다.",
            action: "코인을 블랙리스트에 추가"
          },
          sniper: {
            title: "스나이퍼 모드 🎯",
            message: "놀라운 정확도! 승률 {{winRate}}%. 최고의 흐름입니다."
          },
          gold: {
            title: "알파 자산: {{coin}} 💎",
            message: "{{coin}} 을 완벽하게 트레이딩 중입니다. 수익: +${{profit}}."
          },
          neutral: {
            title: "안정적인 비행",
            message: "승률: {{winRate}}%. PnL: ${{pnl}}. 이상 없음."
          }
        },
      
      auth: { welcome: "환영합니다", create: "계정 생성", loginDesc: "터미널 로그인", registerDesc: "$10,000 데모 자금 받기", email: "이메일", password: "비밀번호", placeholderEmail: "name@example.com", placeholderPass: "••••••••", loginBtn: "로그인", registerBtn: "등록", processing: "처리 중...", noAccount: "계정이 없나요?", hasAccount: "계정이 있나요?", switchLogin: "로그인", switchRegister: "가입", error: "서버 오류" },
      guide: guideKO
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    interpolation: { escapeValue: false }
  });

export default i18n;